import React, { useState, useEffect, useRef } from 'react';
import { Home, Milk, BarChart2, Droplet, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Moon, Sun, SunMoon, Wind, Activity, Bell, BellOff, Settings, Scale, Pill, TrendingUp, MoreHorizontal, Info, Pencil, Trash2 } from 'lucide-react';
import { forecastNextNap, SLOT_LABELS } from './napForecast';
import { db } from './firebase';
import { doc, setDoc, onSnapshot, updateDoc, arrayRemove } from 'firebase/firestore';

const notifSupported = typeof Notification !== 'undefined';
const ua = navigator.userAgent;
const isIOS = /iPhone|iPad|iPod/.test(ua);
const isAndroid = /Android/.test(ua);
const notifSettingsPath = isIOS
  ? 'Settings app → Safari → Notifications'
  : isAndroid
  ? 'Chrome menu → Settings → Site settings → Notifications'
  : 'Browser settings → Site settings → Notifications';

// WHO Child Growth Standards, weight-for-age (0–36 months), kg.
// Source: who.int/tools/child-growth-standards/standards/weight-for-age (expanded percentile tables)
const WHO_WEIGHT = {
  boy: [
    [0,2.51,2.60,2.76,3.03,3.35,3.69,4.01,4.21,4.35],
    [1,3.43,3.55,3.74,4.06,4.45,4.87,5.27,5.52,5.69],
    [2,4.39,4.53,4.74,5.12,5.57,6.05,6.51,6.80,7.00],
    [3,5.09,5.23,5.47,5.88,6.37,6.89,7.39,7.70,7.91],
    [4,5.64,5.80,6.05,6.49,7.01,7.56,8.09,8.42,8.64],
    [5,6.08,6.24,6.50,6.96,7.51,8.09,8.64,8.99,9.22],
    [6,6.44,6.62,6.89,7.37,7.94,8.54,9.12,9.49,9.73],
    [7,6.74,6.92,7.21,7.71,8.30,8.93,9.53,9.91,10.16],
    [8,7.01,7.20,7.49,8.01,8.62,9.27,9.90,10.29,10.56],
    [9,7.24,7.43,7.74,8.27,8.90,9.58,10.22,10.63,10.90],
    [10,7.45,7.65,7.96,8.51,9.16,9.86,10.53,10.95,11.22],
    [11,7.66,7.86,8.18,8.74,9.41,10.13,10.82,11.25,11.54],
    [12,7.84,8.05,8.38,8.96,9.65,10.38,11.09,11.53,11.83],
    [15,8.38,8.60,8.95,9.57,10.31,11.11,11.87,12.35,12.68],
    [18,8.87,9.11,9.48,10.15,10.94,11.79,12.61,13.13,13.48],
    [21,9.34,9.59,9.99,10.70,11.55,12.46,13.34,13.90,14.27],
    [24,9.80,10.07,10.49,11.25,12.15,13.12,14.07,14.66,15.06],
    [27,10.25,10.53,10.98,11.78,12.74,13.78,14.79,15.43,15.86],
    [30,10.67,10.96,11.44,12.29,13.30,14.40,15.47,16.16,16.62],
    [33,11.06,11.37,11.87,12.76,13.83,14.99,16.13,16.85,17.34],
    [36,11.44,11.77,12.29,13.22,14.34,15.57,16.77,17.53,18.05],
  ],
  girl: [
    [0,2.44,2.53,2.68,2.93,3.23,3.55,3.85,4.04,4.17],
    [1,3.20,3.31,3.49,3.80,4.17,4.57,4.96,5.21,5.37],
    [2,4.01,4.13,4.34,4.70,5.13,5.60,6.05,6.34,6.53],
    [3,4.60,4.74,4.96,5.36,5.84,6.36,6.86,7.18,7.39],
    [4,5.09,5.24,5.48,5.91,6.43,6.99,7.54,7.88,8.12],
    [5,5.48,5.64,5.89,6.35,6.90,7.49,8.07,8.45,8.70],
    [6,5.82,5.98,6.25,6.73,7.30,7.93,8.55,8.94,9.20],
    [7,6.09,6.27,6.55,7.04,7.64,8.30,8.94,9.36,9.63],
    [8,6.35,6.53,6.81,7.33,7.95,8.64,9.31,9.74,10.04],
    [9,6.57,6.75,7.05,7.58,8.23,8.94,9.64,10.09,10.39],
    [10,6.77,6.96,7.26,7.81,8.48,9.21,9.94,10.40,10.72],
    [11,6.96,7.16,7.47,8.03,8.72,9.48,10.23,10.71,11.04],
    [12,7.14,7.34,7.66,8.24,8.95,9.72,10.50,10.99,11.33],
    [15,7.67,7.88,8.23,8.85,9.60,10.44,11.28,11.82,12.18],
    [18,8.17,8.40,8.77,9.43,10.23,11.13,12.02,12.60,12.99],
    [21,8.67,8.91,9.30,9.99,10.85,11.80,12.76,13.37,13.79],
    [24,9.16,9.41,9.82,10.56,11.47,12.49,13.50,14.16,14.61],
    [27,9.65,9.92,10.35,11.14,12.10,13.18,14.26,14.96,15.44],
    [30,10.11,10.39,10.85,11.68,12.71,13.85,14.99,15.74,16.25],
    [33,10.54,10.84,11.33,12.20,13.28,14.49,15.70,16.50,17.04],
    [36,10.96,11.28,11.79,12.71,13.85,15.13,16.42,17.26,17.84],
  ],
};

const interpWho = (gender, ageMonths) => {
  const table = WHO_WEIGHT[gender];
  const age = Math.max(0, Math.min(36, ageMonths));
  let lo = table[0], hi = table[table.length - 1];
  for (let i = 0; i < table.length - 1; i++) {
    if (table[i][0] <= age && table[i + 1][0] >= age) { lo = table[i]; hi = table[i + 1]; break; }
  }
  const t = hi[0] === lo[0] ? 0 : (age - lo[0]) / (hi[0] - lo[0]);
  return lo.map((v, i) => i === 0 ? age : lo[i] + t * (hi[i] - lo[i]));
};

// Column 5 of a WHO row is the 50th percentile (see PCTLS in GrowthTab).
const whoMedianWeightKg = (gender, ageWeeks) =>
  interpWho(gender === 'girl' ? 'girl' : 'boy', ageWeeks / 4.345)[5];

// AAP and CDC give 150 ml per kg of body weight per day as a starting estimate.
// It is a reference point, not a quota: healthy babies who take a bit more or
// less and are growing along their percentile are fine, and WHO recommends
// feeding term infants on demand. NOT reviewed by a clinician.
const ML_PER_KG_DAY = 150;

// Most healthy term infants plateau around 32 oz (~960ml) a day from about month
// 4, and taking more than that doesn't benefit them. Without this, 150ml/kg keeps
// climbing with weight and reaches ~1450ml by the first birthday, by which point
// solids have replaced much of the milk anyway. NOT reviewed by a clinician: this
// cap and ML_PER_KG_DAY are the two numbers to put in front of one.
const DAILY_ML_CAP = 960;

export default function App() {
  const [unit, setUnit] = useState('ml');
  const [feeds, setFeeds] = useState([]);
  const [babyAge, setBabyAge] = useState(2);
  // Parent-set daily goal in ml. null = fall back to the weight/age estimate.
  // dailyGoalInput holds the raw field text in the user's current unit, so typing
  // in oz isn't fought by round-tripping through ml on every keystroke.
  const [dailyGoal, setDailyGoal] = useState(null);
  const [dailyGoalInput, setDailyGoalInput] = useState('');
  const [babyName, setBabyName] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [nextFeedTime, setNextFeedTime] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [statsDateKey, setStatsDateKey] = useState(() => new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }));
  const [isBabyAwake, setIsBabyAwake] = useState(false);
  const [wakeStartTime, setWakeStartTime] = useState(null);
  // Running direct-latch timer. Persisted like wakeState so it survives closing
  // the app and shows as running on the partner's device. sideStart is when the
  // current side began; leftMs/rightMs are the sides already banked this session.
  const [latchState, setLatchState] = useState({ active: false, side: 'L', sideStart: null, leftMs: 0, rightMs: 0 });
  const [feedMode, setFeedMode] = useState('bottle');
  const [, setLatchTick] = useState(0);
  const [wakeWindows, setWakeWindows] = useState([]);
  // Actual sleep sessions. Wake windows record awake periods; naps record the
  // sleep between them, logged when the baby is toggled from asleep back to awake.
  const [naps, setNaps] = useState([]);
  const [wakeElapsed, setWakeElapsed] = useState('');
  const [roomCode, setRoomCode] = useState(() => localStorage.getItem('roomCode') || '');
  const [roomInput, setRoomInput] = useState('');
  const [roomError, setRoomError] = useState('');
  const [roomLoading, setRoomLoading] = useState(false);
  const [notifPermission, setNotifPermission] = useState(notifSupported ? Notification.permission : 'unsupported');
  const [notifMuted, setNotifMuted] = useState(() => localStorage.getItem('notifMuted') === 'true');
  const [showBellTooltip, setShowBellTooltip] = useState(false);

  const [showFeeds, setShowFeeds] = useState(false);
  const [showSleep, setShowSleep] = useState(false);
  const [showDiapers, setShowDiapers] = useState(false);
  const [showPumping, setShowPumping] = useState(false);
  const [timeUntilFeed, setTimeUntilFeed] = useState('');
  const notificationFired = useRef(false);
  const settingsLoaded = useRef(false);
  // True while the goal field has focus. Each keystroke syncs to Firebase, and the
  // snapshot echoes back, so without this the partial value ("7" of "720") is
  // written over what the parent is still typing.
  const goalEditing = useRef(false);
  // Long-press on the Nap button opens the "log a past nap" form. napLongPressed
  // suppresses the click-toggle that would otherwise fire on release.
  const napPressTimer = useRef(null);
  const napLongPressed = useRef(false);
  const deferredInstallPrompt = useRef(null);
  const [installable, setInstallable] = useState(false);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
  const [diapers, setDiapers] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [quickLogModal, setQuickLogModal] = useState(null); // null | 'feed' | 'diaper' | 'pump' | 'weight' | 'medicine'
  const [pumpLeft, setPumpLeft] = useState('');
  const [pumpRight, setPumpRight] = useState('');
  const [weights, setWeights] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [weightInput, setWeightInput] = useState('');
  const [weightG, setWeightG] = useState('');
  const [medicineName, setMedicineName] = useState('');
  const [medicineDose, setMedicineDose] = useState('');
  const [logTime, setLogTime] = useState('');
  const [diaperPendingType, setDiaperPendingType] = useState(null);
  const [logDate, setLogDate] = useState('');
  const [sleepStartDate, setSleepStartDate] = useState('');
  const [sleepStartTime, setSleepStartTime] = useState('');
  const [sleepEndDate, setSleepEndDate] = useState('');
  const [sleepEndTime, setSleepEndTime] = useState('');
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [fromCache, setFromCache] = useState(false);
  const [showWeights, setShowWeights] = useState(false);
  const [showMedicines, setShowMedicines] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'system');
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
  const [splashVisible, setSplashVisible] = useState(true);
  const [splashFading, setSplashFading] = useState(false);
  const [showInstallNudge, setShowInstallNudge] = useState(false);
  const [babyDOB, setBabyDOB] = useState('');
  const dobInputRef = useRef(null);
  const [babyGender, setBabyGender] = useState('boy');
  const [entryActionSheet, setEntryActionSheet] = useState(null); // { type, entry }
  const [editingEntry, setEditingEntry] = useState(null); // { type, entry }
  const [confirmDeleteWeight, setConfirmDeleteWeight] = useState(null); // timestamp pending a 2nd-tap confirm

  const effectiveAge = babyDOB
    ? Math.max(0, Math.floor((Date.now() - new Date(babyDOB)) / (7 * 24 * 60 * 60 * 1000)))
    : babyAge;

  const isDark = theme === 'dark' || (theme === 'system' && systemDark);
  const ACCENT = '#AFDAEB';
  const ACCENT_TEXT = '#1a6a85';
  const BG = isDark ? '#000000' : '#F2F2F7';
  const CARD = isDark ? '#1C1C1E' : '#FFFFFF';
  const TEXT = isDark ? '#FFFFFF' : '#1C1C1E';
  const TEXT2 = '#8E8E93';
  const BORDER = isDark ? '#3A3A3C' : '#E5E5EA';
  const GREEN = '#34C759';
  const RED = '#FF3B30';
  const AMBER = '#FF9500';
  const ACCENT_BG = isDark ? 'rgba(175,218,235,0.2)' : '#E4F5FB';
  const FEED_BG = isDark ? 'rgba(175,218,235,0.15)' : '#E4F5FB';
  const DIAPER_BG = isDark ? 'rgba(255,149,0,0.15)' : '#FFF3E0';
  const PUMP_BG = isDark ? 'rgba(52,199,89,0.15)' : '#E8FAF0';
  const WAKE_BG = isDark ? 'rgba(255,149,0,0.12)' : '#FFF8EC';
  const OVER_BG = isDark ? 'rgba(255,59,48,0.15)' : '#FFF0EE';
  const WEIGHT_COLOR = '#30B0C7';
  const MED_COLOR = '#FF2D55';
  const WEIGHT_BG = isDark ? 'rgba(48,176,199,0.15)' : '#E0F7FA';
  const MED_BG = isDark ? 'rgba(255,45,85,0.15)' : '#FFF0F3';

  // Firestore sync helper
  const syncRoom = (code, data) => {
    setDoc(doc(db, 'rooms', code), data, { merge: true }).catch(console.error);
  };

  // Room management
  const generateCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

  const createRoom = async () => {
    setRoomLoading(true);
    setRoomError('');
    try {
      const code = generateCode();
      const initial = {
        feeds, diapers, pumps, weights, medicines, wakeWindows, naps,
        wakeState: { awake: isBabyAwake, startTime: wakeStartTime ? wakeStartTime.toISOString() : null },
        settings: { unit, babyAge, babyName, babyDOB, babyGender, dailyGoal },
      };
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Connection timed out — check Firebase config')), 10000));
      await Promise.race([setDoc(doc(db, 'rooms', code), initial), timeout]);
      localStorage.setItem('roomCode', code);
      setRoomCode(code);
    } catch (e) {
      setRoomError(`Error: ${e.message}`);
    }
    setRoomLoading(false);
  };

  const joinRoom = async () => {
    const code = roomInput.trim().toUpperCase();
    if (!code) return;
    setRoomLoading(true);
    setRoomError('');
    try {
      const { getDoc } = await import('firebase/firestore');
      const snap = await getDoc(doc(db, 'rooms', code));
      if (!snap.exists()) {
        setRoomError('Room not found. Check the code and try again.');
      } else {
        localStorage.setItem('roomCode', code);
        setRoomCode(code);
      }
    } catch {
      setRoomError('Failed to join room. Check your connection.');
    }
    setRoomLoading(false);
  };

  // Next feed time = 3h after whichever feed is chronologically most recent
  // (by timestamp, not array position/insertion order — a retroactively logged
  // feed can land anywhere in the array, and may or may not be the newest one).
  const getNextFeedTime = (feedsArr) => {
    if (!feedsArr || feedsArr.length === 0) return null;
    const latest = feedsArr.reduce((a, b) => (new Date(b.timestamp) > new Date(a.timestamp) ? b : a));
    const t = new Date(latest.timestamp);
    t.setHours(t.getHours() + 3);
    return t;
  };

  // Real-time sync from Firestore
  useEffect(() => {
    if (!roomCode) return;
    const unsub = onSnapshot(doc(db, 'rooms', roomCode), (snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      if (d.feeds) {
        setFeeds(d.feeds);
        setNextFeedTime(getNextFeedTime(d.feeds));
      }
      if (d.settings) {
        setUnit(d.settings.unit || 'ml');
        setBabyAge(d.settings.babyAge != null ? d.settings.babyAge : 2);
        setBabyName(d.settings.babyName || '');
        setBabyDOB(d.settings.babyDOB || '');
        setBabyGender(d.settings.babyGender || 'boy');
        if (!goalEditing.current) {
          setDailyGoal(d.settings.dailyGoal != null ? d.settings.dailyGoal : null);
          setDailyGoalInput(d.settings.dailyGoal != null
            ? String(d.settings.unit === 'oz' ? mlToOz(d.settings.dailyGoal) : d.settings.dailyGoal)
            : '');
        }
        settingsLoaded.current = true;
      }
      if (d.diapers) setDiapers(d.diapers);
      if (d.pumps) setPumps(d.pumps);
      if (d.weights) setWeights(d.weights);
      if (d.medicines) setMedicines(d.medicines);
      if (d.wakeWindows) setWakeWindows(d.wakeWindows);
      if (d.naps) setNaps(d.naps);
      if (d.latchState) setLatchState(d.latchState);
      if (d.wakeState) {
        setIsBabyAwake(d.wakeState.awake);
        setWakeStartTime(d.wakeState.startTime ? new Date(d.wakeState.startTime) : null);
      }
      localStorage.setItem('teambaby_cache', JSON.stringify(d));
      setFromCache(false);
    });
    return () => unsub();
  }, [roomCode]);

  // Splash screen auto-dismiss
  useEffect(() => {
    const fadeTimer = setTimeout(() => setSplashFading(true), 1800);
    const hideTimer = setTimeout(() => setSplashVisible(false), 2350);
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, []);

  // Install nudge: show after first feed logged, once only
  useEffect(() => {
    if (isStandalone) return;
    if (localStorage.getItem('teambaby_nudge_dismissed')) return;
    if (feeds.length >= 1) setShowInstallNudge(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeds.length]);

  // Save settings
  useEffect(() => {
    if (!roomCode || !settingsLoaded.current) return;
    syncRoom(roomCode, { settings: { unit, babyAge: effectiveAge, babyName, babyDOB, babyGender, dailyGoal } });
  }, [unit, effectiveAge, babyName, babyDOB, babyGender, dailyGoal, roomCode]);

  // Re-render once a second while a latch is running so the elapsed time ticks.
  useEffect(() => {
    if (!latchState.active) return;
    const id = setInterval(() => setLatchTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [latchState.active]);

  // Redisplay the goal field in the newly selected unit when the user switches.
  useEffect(() => {
    setDailyGoal((ml) => {
      setDailyGoalInput(ml != null ? String(unit === 'oz' ? mlToOz(ml) : ml) : '');
      return ml;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      if (nextFeedTime) {
        const diff = nextFeedTime - new Date();
        if (diff <= 0) {
          setTimeUntilFeed('Feed time!');
          if (!notificationFired.current && notifSupported && Notification.permission === 'granted' && !notifMuted) {
            notificationFired.current = true;
            new Notification('Time to feed! 🍼', {
              body: `It's been 3 hours — ${babyName || 'baby'} might be hungry!`,
              icon: '/favicon.ico',
            });
          }
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          setTimeUntilFeed(`${hours}h ${minutes}m`);
        }
      } else {
        setTimeUntilFeed('Log first feed');
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [nextFeedTime, babyName, notifMuted]);

  // System dark mode listener
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const handler = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Android PWA install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); deferredInstallPrompt.current = e; setInstallable(true); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Load cached Firestore data on mount (before Firebase arrives)
  useEffect(() => {
    const raw = localStorage.getItem('teambaby_cache');
    if (!raw) return;
    try {
      const d = JSON.parse(raw);
      if (d.feeds) {
        setFeeds(d.feeds);
        setNextFeedTime(getNextFeedTime(d.feeds));
      }
      if (d.settings) {
        setUnit(d.settings.unit || 'ml');
        setBabyAge(d.settings.babyAge != null ? d.settings.babyAge : 2);
        setBabyName(d.settings.babyName || '');
        setBabyDOB(d.settings.babyDOB || '');
        setBabyGender(d.settings.babyGender || 'boy');
        if (!goalEditing.current) {
          setDailyGoal(d.settings.dailyGoal != null ? d.settings.dailyGoal : null);
          setDailyGoalInput(d.settings.dailyGoal != null
            ? String(d.settings.unit === 'oz' ? mlToOz(d.settings.dailyGoal) : d.settings.dailyGoal)
            : '');
        }
      }
      if (d.diapers) setDiapers(d.diapers);
      if (d.pumps) setPumps(d.pumps);
      if (d.weights) setWeights(d.weights);
      if (d.medicines) setMedicines(d.medicines);
      if (d.wakeWindows) setWakeWindows(d.wakeWindows);
      if (d.naps) setNaps(d.naps);
      if (d.latchState) setLatchState(d.latchState);
      if (d.wakeState) {
        setIsBabyAwake(d.wakeState.awake);
        setWakeStartTime(d.wakeState.startTime ? new Date(d.wakeState.startTime) : null);
      }
      setFromCache(true);
    } catch (e) {}
  }, []);

  // Online / offline detection
  useEffect(() => {
    const up = () => setIsOnline(true);
    const down = () => setIsOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  // "YYYY-MM-DD" in local time, not UTC — toISOString() runs on UTC and lands on the
  // wrong calendar day for part of the day in any timezone ahead of UTC (e.g. a 2am
  // feed in Singapore, UTC+8, would otherwise default/max out to yesterday's date).
  const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  // Reset log time/date to "now" whenever a modal opens for a NEW entry.
  // Skipped when editing, otherwise this clobbers the values openEditEntry just
  // prefilled from the existing entry (date/time snapping back to "now" right
  // after opening, right after Sleep's start/end fields being reset to defaults).
  useEffect(() => {
    if (quickLogModal && !editingEntry) {
      setDiaperPendingType(null);
      const now = new Date();
      const today = localDateStr(now);
      const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setLogTime(hhmm);
      setLogDate(today);
      if (quickLogModal === 'sleep') {
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
        setSleepStartDate(localDateStr(oneHourAgo));
        setSleepStartTime(`${String(oneHourAgo.getHours()).padStart(2, '0')}:${String(oneHourAgo.getMinutes()).padStart(2, '0')}`);
        setSleepEndDate(today);
        setSleepEndTime(hhmm);
      }
    }
  }, [quickLogModal, editingEntry]);

  const getLogTimestamp = () => {
    const base = logDate ? parseLocalDate(logDate) : new Date();
    if (logTime) {
      const [h, m] = logTime.split(':').map(Number);
      base.setHours(h, m, 0, 0);
    } else {
      const now = new Date();
      base.setHours(now.getHours(), now.getMinutes(), 0, 0);
    }
    return base.toISOString();
  };

  // Live timer for the current state. wakeStartTime marks the start of whichever
  // state is active — awake time when awake, nap time when napping — so the Nap
  // button can count up in both directions.
  useEffect(() => {
    const interval = setInterval(() => {
      if (wakeStartTime) {
        const diff = Date.now() - new Date(wakeStartTime).getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setWakeElapsed(hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`);
      } else {
        setWakeElapsed('');
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [wakeStartTime]);

  const getRecommendedWakeWindow = () => {
    if (effectiveAge <= 4) return { min: 45, max: 60 };
    if (effectiveAge <= 8) return { min: 60, max: 90 };
    if (effectiveAge <= 16) return { min: 75, max: 120 };
    if (effectiveAge <= 24) return { min: 90, max: 180 };
    return { min: 120, max: 180 };
  };

  const getWakeStatus = () => {
    if (!isBabyAwake || !wakeStartTime) return null;
    const elapsedMin = (Date.now() - new Date(wakeStartTime).getTime()) / 60000;
    const { min, max } = getRecommendedWakeWindow();
    if (elapsedMin < min * 0.75) return { color: GREEN, label: 'Within window' };
    if (elapsedMin < max) return { color: AMBER, label: 'Nap soon' };
    return { color: RED, label: 'Nap overdue' };
  };

  const toggleWakeState = () => {
    if (!isBabyAwake) {
      const now = new Date();
      // Waking up ends the nap that was running — log it. Skip when there's no
      // nap start (the app's very first toggle from its default state).
      const napPatch = {};
      if (wakeStartTime) {
        const updatedNaps = [...naps, { start: wakeStartTime.toISOString(), end: now.toISOString() }];
        setNaps(updatedNaps);
        napPatch.naps = updatedNaps;
      }
      setIsBabyAwake(true);
      setWakeStartTime(now);
      syncRoom(roomCode, { ...napPatch, wakeState: { awake: true, startTime: now.toISOString() } });
    } else {
      const ended = new Date();
      const entry = { start: wakeStartTime.toISOString(), end: ended.toISOString() };
      const updated = [...wakeWindows, entry];
      setWakeWindows(updated);
      setIsBabyAwake(false);
      // Keep a timestamp so the asleep state shows a live nap timer; the awake
      // window we just closed is what got logged above.
      setWakeStartTime(ended);
      syncRoom(roomCode, { wakeWindows: updated, wakeState: { awake: false, startTime: ended.toISOString() } });
    }
  };

  // Editing an existing wake window updates it in place. A brand-new entry from
  // the Nap button's "log a past nap" is an actual nap (sleep period), logged to
  // the naps list — not a wake window.
  const logSleepWindow = () => {
    const buildDT = (date, time) => {
      const d = parseLocalDate(date);
      const [h, m] = time.split(':').map(Number);
      d.setHours(h, m, 0, 0);
      return d;
    };
    const start = buildDT(sleepStartDate, sleepStartTime);
    const end = buildDT(sleepEndDate, sleepEndTime);
    if (end <= start) return;
    const newEntry = { start: start.toISOString(), end: end.toISOString() };
    if (editingEntry?.type === 'sleep') {
      const updated = wakeWindows.map(w => w.start === editingEntry.entry.start ? newEntry : w).sort((a, b) => new Date(a.start) - new Date(b.start));
      setEditingEntry(null);
      setWakeWindows(updated);
      syncRoom(roomCode, { wakeWindows: updated });
    } else {
      const updated = [...naps, newEntry].sort((a, b) => new Date(a.start) - new Date(b.start));
      setNaps(updated);
      syncRoom(roomCode, { naps: updated });
    }
    setQuickLogModal(null);
  };

  const formatDuration = (ms) => {
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Feeds that carry a measurable volume. Direct-latch feeds record duration only,
  // so every ml calculation (totals, goal %, suggested amount, averages) uses this
  // list rather than `feeds`.
  const bottleFeeds = feeds.filter((f) => !f.latch);

  // mm:ss while a latch runs — formatDuration's "0m" is too coarse to show it ticking.
  const formatClock = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${m}:${String(s).padStart(2, '0')}`;
  };

  // Which side the previous latch ended on, so the mother knows where to start next.
  const lastLatchSide = (() => {
    const latches = feeds.filter((f) => f.latch);
    if (latches.length === 0) return null;
    const latest = latches.reduce((a, b) => (new Date(b.timestamp) > new Date(a.timestamp) ? b : a));
    return latest.lastSide || null;
  })();
  // formatDuration floors to minutes, so a short comfort nurse would read as "0m".
  const formatLatchMs = (ms) => (ms < 60000 ? `${Math.round(ms / 1000)}s` : formatDuration(ms));
  const latchLabel = (f) => {
    const total = (f.leftMs || 0) + (f.rightMs || 0);
    const sides = [f.leftMs ? `L ${formatLatchMs(f.leftMs)}` : null, f.rightMs ? `R ${formatLatchMs(f.rightMs)}` : null]
      .filter(Boolean)
      .join(' · ');
    return { total: formatLatchMs(total), sides };
  };

  const getFormulaRecommendedAmount = () => {
    if (weights.length > 0) {
      const latestKg = weights[weights.length - 1].grams / 1000;
      return Math.round((150 * latestKg) / 8);
    }
    if (effectiveAge <= 1) return 60;
    if (effectiveAge <= 2) return 90;
    if (effectiveAge <= 4) return 120;
    if (effectiveAge <= 8) return 150;
    return 180;
  };

  // Amount the user logs most often recently, so "Suggested" reflects real habits
  // instead of just the weight/age formula. If the last 2+ feeds in a row share an
  // amount, that's treated as a deliberate habit change (e.g. baby feeding bigger
  // now) and used immediately, so it isn't drowned out by older history. Otherwise
  // falls back to the most common amount (count >= 2) among the last 30 feeds.
  const getModeFeedAmount = () => {
    // Latch feeds have no measurable volume, so they must not drag the suggestion.
    const feeds = bottleFeeds;
    if (feeds.length < 2) return null;
    const lastAmount = feeds[feeds.length - 1].amount;
    let streak = 1;
    for (let i = feeds.length - 2; i >= 0 && feeds[i].amount === lastAmount; i--) streak++;
    if (streak >= 2) return lastAmount;

    if (feeds.length < 3) return null;
    const recentFeeds = feeds.slice(-30);
    const counts = new Map();
    recentFeeds.forEach((f, i) => {
      const entry = counts.get(f.amount) || { count: 0, lastIndex: -1 };
      entry.count += 1;
      entry.lastIndex = i;
      counts.set(f.amount, entry);
    });
    let best = null;
    counts.forEach((entry, amount) => {
      if (!best || entry.count > best.count || (entry.count === best.count && entry.lastIndex > best.lastIndex)) {
        best = { amount, count: entry.count, lastIndex: entry.lastIndex };
      }
    });
    return best && best.count >= 2 ? best.amount : null;
  };

  const getRecommendedAmount = () => getModeFeedAmount() ?? getFormulaRecommendedAmount();

  const mlToOz = (ml) => (ml * 0.033814).toFixed(1);
  const ozToMl = (oz) => Math.round(oz * 29.5735);
  const convert = (amount) => (unit === 'ml' ? amount : mlToOz(amount));

  const requestNotifications = async () => {
    if (!notifSupported) return;
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      setNotifPermission(permission);
    }
  };

  const logFeed = async (amount) => {
    await requestNotifications();
    notificationFired.current = false;
    const newFeed = {
      timestamp: getLogTimestamp(),
      amount: unit === 'oz' ? ozToMl(amount) : amount,
      unit,
    };
    const updatedFeeds = editingEntry?.type === 'feed'
      ? feeds.map(f => f.timestamp === editingEntry.entry.timestamp ? newFeed : f)
      : [...feeds, newFeed];
    if (editingEntry?.type === 'feed') { setEditingEntry(null); setLogDate(''); setLogTime(''); }
    setFeeds(updatedFeeds);
    syncRoom(roomCode, { feeds: updatedFeeds });
    setNextFeedTime(getNextFeedTime(updatedFeeds));
    setCustomAmount('');
    setActiveTab('home');
  };

  // ── Direct latch timer ───────────────────────────────────
  // Milliseconds banked on each side, including the side currently running.
  const latchTotals = () => {
    const running = latchState.active && latchState.sideStart
      ? Date.now() - new Date(latchState.sideStart).getTime()
      : 0;
    return {
      left: latchState.leftMs + (latchState.side === 'L' ? running : 0),
      right: latchState.rightMs + (latchState.side === 'R' ? running : 0),
    };
  };

  const pushLatchState = (next) => {
    setLatchState(next);
    syncRoom(roomCode, { latchState: next });
  };

  const startLatch = async (side) => {
    await requestNotifications();
    notificationFired.current = false;
    pushLatchState({ active: true, side, sideStart: new Date().toISOString(), leftMs: 0, rightMs: 0 });
  };

  // Bank the running side's time, then start the other one.
  const switchLatchSide = () => {
    if (!latchState.active) return;
    const { left, right } = latchTotals();
    pushLatchState({
      active: true,
      side: latchState.side === 'L' ? 'R' : 'L',
      sideStart: new Date().toISOString(),
      leftMs: left,
      rightMs: right,
    });
  };

  const cancelLatch = () => {
    pushLatchState({ active: false, side: 'L', sideStart: null, leftMs: 0, rightMs: 0 });
  };

  // A latch feed carries duration per side and deliberately no `amount` — there's
  // no way to measure volume at the breast, so it stays out of ml totals entirely.
  const stopLatch = () => {
    if (!latchState.active) return;
    const { left, right } = latchTotals();
    if (left + right < 1000) { cancelLatch(); return; }
    const newFeed = {
      timestamp: new Date().toISOString(),
      latch: true,
      leftMs: Math.round(left),
      rightMs: Math.round(right),
      lastSide: latchState.side,
    };
    const updatedFeeds = [...feeds, newFeed];
    setFeeds(updatedFeeds);
    setLatchState({ active: false, side: 'L', sideStart: null, leftMs: 0, rightMs: 0 });
    syncRoom(roomCode, {
      feeds: updatedFeeds,
      latchState: { active: false, side: 'L', sideStart: null, leftMs: 0, rightMs: 0 },
    });
    setNextFeedTime(getNextFeedTime(updatedFeeds));
    setQuickLogModal(null);
    setActiveTab('home');
  };

  const logDiaper = (type, color) => {
    const newEntry = { timestamp: getLogTimestamp(), type, ...(color ? { color } : {}) };
    const updated = editingEntry?.type === 'diaper'
      ? diapers.map(d => d.timestamp === editingEntry.entry.timestamp ? newEntry : d)
      : [...diapers, newEntry];
    if (editingEntry?.type === 'diaper') { setEditingEntry(null); setLogDate(''); setLogTime(''); }
    setDiapers(updated);
    syncRoom(roomCode, { diapers: updated });
    setDiaperPendingType(null);
    setQuickLogModal(null);
  };

  const logPump = (left, right) => {
    const toMl = (v) => v ? (unit === 'oz' ? ozToMl(parseFloat(v)) : parseFloat(v)) : 0;
    const leftMl = toMl(left);
    const rightMl = toMl(right);
    const newEntry = { timestamp: getLogTimestamp(), amount: leftMl + rightMl, left: leftMl, right: rightMl };
    const updated = editingEntry?.type === 'pump'
      ? pumps.map(p => p.timestamp === editingEntry.entry.timestamp ? newEntry : p)
      : [...pumps, newEntry];
    if (editingEntry?.type === 'pump') { setEditingEntry(null); setLogDate(''); setLogTime(''); }
    setPumps(updated);
    syncRoom(roomCode, { pumps: updated });
    setPumpLeft(''); setPumpRight('');
    setQuickLogModal(null);
  };


  const logWeight = (grams) => {
    const newEntry = { timestamp: getLogTimestamp(), grams };
    const updated = editingEntry?.type === 'weight'
      ? weights.map(w => w.timestamp === editingEntry.entry.timestamp ? newEntry : w)
      : [...weights, newEntry];
    if (editingEntry?.type === 'weight') { setEditingEntry(null); setLogDate(''); setLogTime(''); }
    setWeights(updated);
    syncRoom(roomCode, { weights: updated });
    setWeightInput(''); setWeightG('');
    setQuickLogModal(null);
  };

  const logMedicine = (name, dose) => {
    const newEntry = { timestamp: getLogTimestamp(), name, dose };
    const updated = editingEntry?.type === 'medicine'
      ? medicines.map(m => m.timestamp === editingEntry.entry.timestamp ? newEntry : m)
      : [...medicines, newEntry];
    if (editingEntry?.type === 'medicine') { setEditingEntry(null); setLogDate(''); setLogTime(''); }
    setMedicines(updated);
    syncRoom(roomCode, { medicines: updated });
    setMedicineName(''); setMedicineDose('');
    setQuickLogModal(null);
  };

  const deleteEntry = (type, entry) => {
    const roomRef = doc(db, 'rooms', roomCode);
    if (type === 'feed') {
      setFeeds(prev => prev.filter(f => f.timestamp !== entry.timestamp));
      updateDoc(roomRef, { feeds: arrayRemove(entry) }).catch(console.error);
    } else if (type === 'diaper') {
      setDiapers(prev => prev.filter(d => d.timestamp !== entry.timestamp));
      updateDoc(roomRef, { diapers: arrayRemove(entry) }).catch(console.error);
    } else if (type === 'sleep') {
      setWakeWindows(prev => prev.filter(w => w.start !== entry.start));
      updateDoc(roomRef, { wakeWindows: arrayRemove(entry) }).catch(console.error);
    } else if (type === 'nap') {
      setNaps(prev => prev.filter(n => n.start !== entry.start));
      updateDoc(roomRef, { naps: arrayRemove(entry) }).catch(console.error);
    } else if (type === 'pump') {
      setPumps(prev => prev.filter(p => p.timestamp !== entry.timestamp));
      updateDoc(roomRef, { pumps: arrayRemove(entry) }).catch(console.error);
    } else if (type === 'weight') {
      setWeights(prev => prev.filter(w => w.timestamp !== entry.timestamp));
      updateDoc(roomRef, { weights: arrayRemove(entry) }).catch(console.error);
    } else if (type === 'medicine') {
      setMedicines(prev => prev.filter(m => m.timestamp !== entry.timestamp));
      updateDoc(roomRef, { medicines: arrayRemove(entry) }).catch(console.error);
    }
    setEntryActionSheet(null);
  };

  const openEditEntry = (type, entry) => {
    setEntryActionSheet(null);
    setEditingEntry({ type, entry });
    if (type === 'sleep') {
      const s = new Date(entry.start), e = new Date(entry.end);
      setSleepStartDate(localDateStr(s));
      setSleepStartTime(s.toTimeString().slice(0, 5));
      setSleepEndDate(localDateStr(e));
      setSleepEndTime(e.toTimeString().slice(0, 5));
      setQuickLogModal('sleep');
    } else {
      const ts = new Date(entry.timestamp);
      setLogDate(localDateStr(ts));
      setLogTime(ts.toTimeString().slice(0, 5));
      if (type === 'feed') {
        // Latch feeds have no amount to edit; the timer produced the durations.
        if (entry.latch) { setEditingEntry(null); return; }
        setCustomAmount(String(unit === 'oz' ? mlToOz(entry.amount) : entry.amount));
        setQuickLogModal('feed');
      } else if (type === 'diaper') {
        setDiaperPendingType(null);
        setQuickLogModal('diaper');
      } else if (type === 'pump') {
        setPumpLeft(String(unit === 'ml' ? Math.round(entry.left || 0) : mlToOz(entry.left || 0)));
        setPumpRight(String(unit === 'ml' ? Math.round(entry.right || 0) : mlToOz(entry.right || 0)));
        setQuickLogModal('pump');
      } else if (type === 'weight') {
        setWeightInput(String(Math.floor(entry.grams / 1000)));
        setWeightG(entry.grams % 1000 > 0 ? String(entry.grams % 1000) : '');
        setQuickLogModal('weight');
      } else if (type === 'medicine') {
        setMedicineName(entry.name);
        setMedicineDose(entry.dose || '');
        setQuickLogModal('medicine');
      }
    }
  };

  // Per-feed suggestion. Driven by what the parent actually logs, which is the
  // right input for "how much to put in the next bottle".
  const recommended = getRecommendedAmount();

  // Daily estimate. Deliberately NOT the per-feed figure multiplied by a feed
  // count: that conflated how big a feed is with how many happen in a day, and
  // ran roughly 40% over the AAP/CDC reference for a 7kg baby. Anchored to weight
  // instead, falling back to WHO median weight-for-age when none is logged.
  const dailyIntake = (() => {
    const fromKg = (kg, basis) => {
      const raw = Math.round(ML_PER_KG_DAY * kg);
      return { ml: Math.min(raw, DAILY_ML_CAP), basis, kg, capped: raw > DAILY_ML_CAP };
    };
    if (weights.length > 0) return fromKg(weights[weights.length - 1].grams / 1000, 'weight');
    if (!babyDOB) return null;
    return fromKg(whoMedianWeightKg(babyGender, effectiveAge), 'who');
  })();
  const recommendedDaily = dailyIntake ? dailyIntake.ml : 0;

  // Daily goal shown to the parent. A value they set in Settings always wins; the
  // weight estimate is only a fallback for before they've set one. WHO gives
  // no volume guidance for healthy term infants (it recommends feeding on demand),
  // so this is a reference point the parent owns, not a clinical target.
  const effectiveDailyGoal = dailyGoal > 0 ? dailyGoal : recommendedDaily;
  const isGoalCustom = dailyGoal > 0;

  const calculateFormula = (targetMl) => {
    const scoops = Math.round(targetMl / 30);
    return { scoops, water: scoops * 30 };
  };


  // Circular timer ring (Home tab)
  const getElapsedFraction = () => {
    if (!nextFeedTime) return 0;
    const lastFeedTime = new Date(nextFeedTime.getTime() - 3 * 60 * 60 * 1000);
    const elapsed = Date.now() - lastFeedTime.getTime();
    return Math.min(Math.max(elapsed / (3 * 60 * 60 * 1000), 0), 1);
  };

  // Ring is sized to leave room for the stats column beside it on narrow phones.
  const TR = 78;
  const TSTROKE = 12;
  const TCIRC = 2 * Math.PI * TR;
  const elapsedFrac = getElapsedFraction();
  const timerOffset = TCIRC * (1 - elapsedFrac);
  const timerSize = (TR + TSTROKE) * 2;
  const timerColor = elapsedFrac >= 1 ? RED : ACCENT_TEXT;

  // Daily progress ring (Stats tab)
  const PR = 68;
  const PCIRC = 2 * Math.PI * PR;
  const pSize = (PR + 10) * 2;

  const dateKeyOf = (ts) => new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const getFeedsForDate = (key) => feeds.filter((f) => dateKeyOf(f.timestamp) === key);
  const getWakeWindowsForDate = (key) => wakeWindows.filter((w) => dateKeyOf(w.start) === key);
  const getNapsForDate = (key) => naps.filter((n) => dateKeyOf(n.start) === key);
  const getDiapersForDate = (key) => diapers.filter((d) => dateKeyOf(d.timestamp) === key);
  const getPumpsForDate = (key) => pumps.filter((p) => dateKeyOf(p.timestamp) === key);
  const getWeightsForDate = (key) => weights.filter((w) => dateKeyOf(w.timestamp) === key);
  const getMedicinesForDate = (key) => medicines.filter((m) => dateKeyOf(m.timestamp) === key);

  // Supporting stats shown beside the Home countdown ring. Feeds are appended in
  // insertion order and never sorted, so a backdated entry can sit at the end of
  // the array — order by timestamp rather than trusting array position.
  const homeTodayFeeds = getFeedsForDate(dateKeyOf(Date.now()))
    .slice()
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const lastFeed = feeds.length > 0
    ? feeds.reduce((latest, f) => (new Date(f.timestamp) > new Date(latest.timestamp) ? f : latest))
    : null;
  const todayBottleFeeds = homeTodayFeeds.filter((f) => !f.latch);
  const todayLatchFeeds = homeTodayFeeds.filter((f) => f.latch);
  const todayTotal = todayBottleFeeds.reduce((sum, f) => sum + f.amount, 0);

  // Capped at 100. The estimate is a "has had enough" reference, not a ceiling, so
  // showing 124% would read as overfeeding when the baby is simply within normal
  // variation. There is no such thing as more than enough here.
  const goalPct = effectiveDailyGoal > 0 ? Math.min(100, Math.round((todayTotal / effectiveDailyGoal) * 100)) : null;

  // Two feeds make one gap, so anything less has no rhythm to report yet.
  const avgFeedGapMs = homeTodayFeeds.length >= 2
    ? (new Date(homeTodayFeeds[homeTodayFeeds.length - 1].timestamp) - new Date(homeTodayFeeds[0].timestamp)) / (homeTodayFeeds.length - 1)
    : null;

  const shiftStatsDate = (deltaDays) => {
    const d = new Date(statsDateKey);
    d.setDate(d.getDate() + deltaDays);
    setStatsDateKey(d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }));
  };


  const tabs = [
    { id: 'home', label: 'Home', Icon: Home },
    { id: 'sleep', label: 'Sleep', Icon: Moon, beta: true },
    { id: 'stats', label: 'Stats', Icon: BarChart2 },
    { id: 'growth', label: 'Growth', Icon: TrendingUp },
    { id: 'settings', label: 'Settings', Icon: Settings },
  ];

  // ── Direct latch timer (inside Log Feed) ─────────────────
  // Called as a function, not <LatchTimer />, matching HomeTab/SettingsTab: all
  // state lives in App, so this is markup rather than a mounted component.
  const LatchTimer = () => {
    const { left, right } = latchTotals();
    const sideBtn = (side, label) => (
      <button key={side} onClick={() => startLatch(side)} style={{
        flex: 1, padding: '22px 8px', border: `1.5px solid ${BORDER}`, borderRadius: 14,
        background: CARD, color: TEXT, fontSize: 17, fontWeight: 700, cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      }}>
        {label}
        <span style={{ fontSize: 11, fontWeight: 500, color: TEXT2 }}>Start</span>
      </button>
    );

    if (!latchState.active) {
      return (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            {sideBtn('L', 'Left')}
            {sideBtn('R', 'Right')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
            <Info size={12} color={TEXT2} />
            <span style={{ fontSize: 12, color: TEXT2 }}>
              {lastLatchSide ? `Last time ended on the ${lastLatchSide === 'L' ? 'left' : 'right'}` : 'Pick the side baby starts on'}
            </span>
          </div>
        </>
      );
    }

    const current = latchState.side === 'L' ? left : right;
    return (
      <>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: TEXT2, marginBottom: 4 }}>
            {latchState.side === 'L' ? 'Left' : 'Right'} side
          </div>
          <div style={{ fontSize: 44, fontWeight: 700, color: ACCENT_TEXT, letterSpacing: -1, lineHeight: 1 }}>
            {formatClock(current)}
          </div>
          <div style={{ fontSize: 13, color: TEXT2, fontWeight: 600, marginTop: 8 }}>
            L {formatClock(left)} · R {formatClock(right)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <button onClick={switchLatchSide} style={{
            flex: 1, padding: '14px', border: `1.5px solid ${BORDER}`, borderRadius: 12,
            background: CARD, color: TEXT, fontSize: 15, fontWeight: 700, cursor: 'pointer',
          }}>Switch to {latchState.side === 'L' ? 'right' : 'left'}</button>
          <button onClick={stopLatch} style={{
            flex: 1, padding: '14px', border: 'none', borderRadius: 12,
            background: ACCENT, color: ACCENT_TEXT, fontSize: 15, fontWeight: 700, cursor: 'pointer',
          }}>Done</button>
        </div>
        <button onClick={cancelLatch} style={{
          width: '100%', padding: '10px', border: 'none', borderRadius: 10, background: 'none',
          color: TEXT2, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Discard</button>
      </>
    );
  };

  // ── Home Tab ─────────────────────────────────────────────
  const HomeTab = () => (
    <div style={{ padding: '32px 20px 24px' }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: TEXT, letterSpacing: -0.5 }}>
            {babyName ? `${babyName}'s Tracker` : "TeamBaby"}
          </h1>
          <button onClick={() => { navigator.clipboard?.writeText(roomCode); }} style={{ background: ACCENT_BG, border: 'none', padding: '3px 10px', borderRadius: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, marginTop: 5 }}>
            <span style={{ fontSize: 12, color: ACCENT_TEXT, fontWeight: 700, letterSpacing: 1.5 }}>Room: {roomCode}</span>
            <span style={{ fontSize: 10, color: ACCENT_TEXT }}>⎘</span>
          </button>
        </div>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => {
              if (notifPermission === 'default') {
                requestNotifications();
              } else if (notifPermission === 'granted') {
                const next = !notifMuted;
                setNotifMuted(next);
                localStorage.setItem('notifMuted', next);
                setShowBellTooltip(false);
              } else {
                setShowBellTooltip(v => !v);
              }
            }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
          >
            {notifPermission === 'granted' && !notifMuted && <span style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, borderRadius: '50%', background: GREEN, border: '1.5px solid white' }} />}
            {notifPermission === 'granted' && !notifMuted
              ? <Bell size={18} color={TEXT2} />
              : <BellOff size={18} color={notifPermission === 'denied' || notifMuted ? RED : TEXT2} />}
          </button>
          {showBellTooltip && (
            <div style={{
              position: 'absolute', top: 38, right: 0, zIndex: 100,
              background: '#1a1a2e', color: 'white',
              fontSize: 12, fontWeight: 500, lineHeight: 1.5,
              padding: '10px 14px', borderRadius: 10,
              width: 220, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}>
              {notifPermission === 'denied' ? (
                <>
                  <div style={{ marginBottom: 6 }}>Notifications are blocked.</div>
                  <div style={{ color: 'rgba(255,255,255,0.65)' }}>To enable: {notifSettingsPath}.</div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: 6 }}>Notifications aren't supported on this browser.</div>
                  <div style={{ color: 'rgba(255,255,255,0.65)' }}>Try opening the app in Safari on iOS or Chrome on Android.</div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Timer ring */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Ring and its caption share a column so "Around ..." sits directly
              under the ring rather than under the whole row. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ position: 'relative', width: timerSize, height: timerSize }}>
              <svg width={timerSize} height={timerSize} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={timerSize / 2} cy={timerSize / 2} r={TR} fill="none" stroke={BORDER} strokeWidth={TSTROKE} />
                <circle
                  cx={timerSize / 2} cy={timerSize / 2} r={TR}
                  fill="none" stroke={timerColor} strokeWidth={TSTROKE}
                  strokeLinecap="round"
                  strokeDasharray={TCIRC}
                  strokeDashoffset={timerOffset}
                  style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }}
                />
              </svg>
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: elapsedFrac >= 1 ? 24 : timeUntilFeed === 'Log first feed' ? 18 : 32, fontWeight: 700, color: timerColor, letterSpacing: -1, lineHeight: 1, textAlign: 'center', padding: '0 12px' }}>
                  {timeUntilFeed}
                </span>
                {timeUntilFeed !== 'Log first feed' && <span style={{ fontSize: 12, color: TEXT2, fontWeight: 500, marginTop: 5 }}>until next feed</span>}
              </div>
            </div>
            {nextFeedTime && (
              <p style={{ margin: 0, fontSize: 15, color: TEXT2, fontWeight: 500, textAlign: 'center' }}>
                Around {nextFeedTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </p>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {lastFeed ? (
              <>
                <div>
                  <div style={{ fontSize: 11, color: TEXT2, fontWeight: 600, letterSpacing: 0.3 }}>Last feed</div>
                  <div style={{ fontSize: 15, color: TEXT, fontWeight: 700, letterSpacing: -0.3 }}>
                    {lastFeed.latch
                      ? `${latchLabel(lastFeed).total} latch`
                      : `${convert(lastFeed.amount)}${unit}`}
                    {' '}· {formatDuration(Date.now() - new Date(lastFeed.timestamp))} ago
                  </div>
                  {lastFeed.latch && latchLabel(lastFeed).sides && (
                    <div style={{ fontSize: 12, color: TEXT2, fontWeight: 600 }}>{latchLabel(lastFeed).sides}</div>
                  )}
                </div>

                <div>
                  <div style={{ fontSize: 11, color: TEXT2, fontWeight: 600, letterSpacing: 0.3 }}>Today</div>
                  <div style={{ fontSize: 15, color: TEXT, fontWeight: 700, letterSpacing: -0.3 }}>
                    {todayBottleFeeds.length > 0 && `${convert(todayTotal)}${unit}`}
                    {todayBottleFeeds.length > 0 && todayLatchFeeds.length > 0 && ' · '}
                    {todayLatchFeeds.length > 0 && `${todayLatchFeeds.length} latch feed${todayLatchFeeds.length === 1 ? '' : 's'}`}
                    {homeTodayFeeds.length === 0 && '—'}
                  </div>
                  {/* No goal line when nothing was bottle-fed: there's no volume to measure. */}
                  {todayBottleFeeds.length > 0 && goalPct !== null && (
                    <div style={{ fontSize: 12, color: goalPct >= 100 ? GREEN : TEXT2, fontWeight: 600 }}>
                      {goalPct}% of {convert(effectiveDailyGoal)}{unit} goal
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ fontSize: 11, color: TEXT2, fontWeight: 600, letterSpacing: 0.3 }}>Rhythm</div>
                  <div style={{ fontSize: 15, color: TEXT, fontWeight: 700, letterSpacing: -0.3 }}>
                    {homeTodayFeeds.length} feed{homeTodayFeeds.length === 1 ? '' : 's'} today
                    {avgFeedGapMs !== null && (
                      <span style={{ color: TEXT2, fontWeight: 600 }}> · avg {formatDuration(avgFeedGapMs)} apart</span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 14, color: TEXT2, fontWeight: 500, lineHeight: 1.5 }}>
                Log a feed to see today's totals and rhythm here.
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Install nudge */}
      {showInstallNudge && (
        <div style={{
          background: ACCENT, borderRadius: 16, padding: '14px 16px',
          marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 2px 8px rgba(26,106,133,0.2)',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: ACCENT_TEXT, marginBottom: 2 }}>Add to your home screen</div>
            <div style={{ fontSize: 12, color: 'rgba(26,106,133,0.75)', lineHeight: 1.4 }}>
              Get one-tap access — no browser bar.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {installable ? (
              <button
                onClick={async () => {
                  deferredInstallPrompt.current.prompt();
                  const { outcome } = await deferredInstallPrompt.current.userChoice;
                  if (outcome === 'accepted') { deferredInstallPrompt.current = null; setInstallable(false); }
                  setShowInstallNudge(false);
                  localStorage.setItem('teambaby_nudge_dismissed', '1');
                }}
                style={{ background: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 700, color: ACCENT_TEXT, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Install
              </button>
            ) : (
              <button
                onClick={() => {
                  setActiveTab('settings');
                  setShowInstallNudge(false);
                  localStorage.setItem('teambaby_nudge_dismissed', '1');
                }}
                style={{ background: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 700, color: ACCENT_TEXT, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Show me
              </button>
            )}
            <button
              onClick={() => { setShowInstallNudge(false); localStorage.setItem('teambaby_nudge_dismissed', '1'); }}
              style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: 'rgba(26,106,133,0.7)', fontSize: 20, lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Quick Log Strip. The Nap tile folds in the old wake-window card: it
          toggles awake/napping on tap, counts up in the current state, and tints
          amber/red as the wake window runs long. Long-press logs a past nap. */}
      {(() => {
        const napStatus = getWakeStatus();
        const napTint = napStatus ? napStatus.color : TEXT;
        const startPress = () => {
          napLongPressed.current = false;
          napPressTimer.current = setTimeout(() => { napLongPressed.current = true; setQuickLogModal('sleep'); }, 500);
        };
        const endPress = () => { if (napPressTimer.current) { clearTimeout(napPressTimer.current); napPressTimer.current = null; } };
        return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { id: 'feed', label: 'Feed', Icon: Milk, color: ACCENT_TEXT, bg: FEED_BG },
          { id: 'diaper', label: 'Diaper', Icon: Wind, color: AMBER, bg: DIAPER_BG },
          { id: 'sleep', label: 'Nap', Icon: Moon, color: ACCENT_TEXT, bg: FEED_BG },
          { id: 'pump', label: 'Pump', Icon: Activity, color: GREEN, bg: PUMP_BG },
          { id: 'weight', label: 'Weight', Icon: Scale, color: WEIGHT_COLOR, bg: WEIGHT_BG },
          { id: 'medicine', label: 'Medicine', Icon: Pill, color: MED_COLOR, bg: MED_BG },
        ].map(({ id, label, Icon, color, bg }) => {
          const isNap = id === 'sleep';
          // Nap tile is state-driven: sun/amber while awake, moon/blue while napping.
          const NapIcon = isNap && isBabyAwake ? Sun : Moon;
          const napIcon = isNap ? NapIcon : Icon;
          const napIconColor = isNap ? (isBabyAwake ? AMBER : ACCENT_TEXT) : color;
          const napBg = isNap ? (isBabyAwake ? WAKE_BG : FEED_BG) : bg;
          // Sun (awake) reads "Awake · Xm"; moon (napping/idle) reads "Nap".
          const napLabel = isNap ? `${isBabyAwake ? 'Awake' : 'Nap'}${wakeStartTime ? ` · ${wakeElapsed || '0m'}` : ''}` : label;
          const IconEl = napIcon;
          return (
          <button key={id}
            onClick={() => {
              if (isNap && napLongPressed.current) { napLongPressed.current = false; return; }
              isNap ? toggleWakeState() : setQuickLogModal(id);
            }}
            onPointerDown={isNap ? startPress : undefined}
            onPointerUp={isNap ? endPress : undefined}
            onPointerLeave={isNap ? endPress : undefined}
            onContextMenu={isNap ? (e) => e.preventDefault() : undefined}
            style={{
              background: CARD, border: 'none', borderRadius: 16, padding: '16px 8px',
              cursor: 'pointer', display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'manipulation',
            }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: napBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconEl size={20} color={napIconColor} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: isNap && napStatus ? napTint : TEXT }}>{napLabel}</span>
          </button>
          );
        })}
      </div>
        );
      })()}


    </div>
  );


  // ── Settings Tab ─────────────────────────────────────────
  // ── Sleep tab (Beta) ─────────────────────────────────────
  // Everything here is descriptive: it reports patterns in what the parent has
  // already logged. It never tells them what to do. "Wake windows" have no
  // clinical evidence base, so a prescriptive framing would be claiming more than
  // the data supports, and the pediatric guidance is to follow the baby's cues
  // rather than a clock. The cue line below is not decoration.
  const SleepTab = () => {
    const name = babyName || 'your baby';
    const f = forecastNextNap({
      naps,
      wakeWindows,
      wakeState: { awake: isBabyAwake, startTime: wakeStartTime ? wakeStartTime.toISOString() : null },
      ageWeeks: effectiveAge,
      now: new Date(),
    });
    const t = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    const Card = ({ children, pad = '18px 20px' }) => (
      <div style={{ background: CARD, borderRadius: 16, padding: pad, marginBottom: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>{children}</div>
    );

    return (
      <div style={{ padding: '0 16px 24px' }}>
        {/* Beta notice. Stated plainly rather than as a badge alone, so a parent
            knows what "beta" actually means for something they'd act on. */}
        <div style={{ background: ACCENT_BG, borderRadius: 14, padding: '13px 16px', marginBottom: 14, display: 'flex', gap: 10 }}>
          <Info size={17} color={ACCENT_TEXT} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.5 }}>
            <strong style={{ fontWeight: 700 }}>Beta.</strong> This feature is still being built and will be wrong sometimes.
            It only describes patterns in what you've logged, and it gets more useful the more you log.
          </div>
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT2, letterSpacing: 0.3, textTransform: 'uppercase', margin: '4px 0 10px' }}>Nap Forecast</div>

        {!babyDOB ? (
          <Card>
            <div style={{ fontSize: 15, color: TEXT, marginBottom: 6, fontWeight: 600 }}>Add a date of birth first</div>
            <div style={{ fontSize: 13.5, color: TEXT2, lineHeight: 1.5 }}>
              Set {name}'s birthday in Settings so the forecast has an age to work from.
            </div>
          </Card>
        ) : f.status === 'asleep' ? (
          <Card>
            <div style={{ fontSize: 15, color: TEXT, fontWeight: 600, marginBottom: 6 }}>{name} is asleep</div>
            <div style={{ fontSize: 13.5, color: TEXT2, lineHeight: 1.5 }}>
              The next forecast appears once you log that {name} is awake again.
            </div>
          </Card>
        ) : f.status === 'unknown' ? (
          <Card>
            <div style={{ fontSize: 15, color: TEXT, fontWeight: 600, marginBottom: 6 }}>Nothing to forecast yet</div>
            <div style={{ fontSize: 13.5, color: TEXT2, lineHeight: 1.5 }}>
              Use the Nap tile on Home to log when {name} falls asleep and wakes. After a few days there's enough to work from.
            </div>
          </Card>
        ) : (
          <Card>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: TEXT, letterSpacing: -0.8 }}>
                {t(f.windowStart)} – {t(f.windowEnd)}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.3, padding: '3px 7px', borderRadius: 6,
                background: f.basis === 'personal' ? ACCENT_BG : (isDark ? '#2C2C2E' : '#F2F2F7'),
                color: f.basis === 'personal' ? ACCENT_TEXT : TEXT2, whiteSpace: 'nowrap',
              }}>
                {f.basis === 'personal' ? `${name.toUpperCase()}'S PATTERN` : 'TYPICAL FOR AGE'}
              </span>
            </div>

            <div style={{ fontSize: 13.5, color: f.status === 'due' ? AMBER : TEXT2, fontWeight: 500, marginBottom: 14 }}>
              {f.status === 'due'
                ? `Window passed ${formatDuration((Date.now() - f.windowEnd) )} ago · awake ${formatDuration(f.awakeMinutes * 60000)}`
                : f.minutesUntilWindow > 0
                  ? `In about ${formatDuration(f.minutesUntilWindow * 60000)} · awake ${formatDuration(f.awakeMinutes * 60000)}`
                  : `Window is open now · awake ${formatDuration(f.awakeMinutes * 60000)}`}
            </div>

            <div style={{ fontSize: 13.5, color: TEXT, lineHeight: 1.55, paddingTop: 13, borderTop: `1px solid ${BORDER}` }}>
              {f.basis === 'personal' ? (
                <>
                  {name} usually falls asleep about {formatDuration(f.expectedMinutes * 60000)} after waking {SLOT_LABELS[f.slot]}.
                  {' '}Based on {f.sampleSize} logged {f.sampleSize === 1 ? 'time' : 'times'} in the last two weeks.
                </>
              ) : (
                <>
                  Not enough logged sleep yet to see {name}'s own pattern {SLOT_LABELS[f.slot]}, so this is a typical
                  range for {effectiveAge} weeks. It narrows once there are a few more naps to go on.
                </>
              )}
            </div>

            {f.adjustment && (
              <div style={{ fontSize: 13, color: TEXT2, lineHeight: 1.5, marginTop: 9 }}>
                {f.priorNapMinutes < f.adjustment.splitPoint
                  ? `That last nap was ${formatDuration(f.priorNapMinutes * 60000)}, on the shorter side for ${name}, and after shorter naps ${name} has usually been ready sooner.`
                  : `That last nap was ${formatDuration(f.priorNapMinutes * 60000)}, on the longer side for ${name}, and after longer naps ${name} has usually gone a bit longer.`}
              </div>
            )}
          </Card>
        )}

        {/* The clinical guidance is to read the baby, not the clock. This stays
            visible in every state rather than being tucked into a disclaimer. */}
        <div style={{ display: 'flex', gap: 9, padding: '0 4px', marginBottom: 22 }}>
          <SunMoon size={15} color={TEXT2} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12.5, color: TEXT2, lineHeight: 1.5 }}>
            {name}'s cues tell you more than the clock does. Yawning, eye-rubbing, staring off, or going quiet
            are worth more than any range here.
          </div>
        </div>

        {f.drift && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT2, letterSpacing: 0.3, textTransform: 'uppercase', margin: '4px 0 10px' }}>Changing pattern</div>
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                <TrendingUp size={17} color={AMBER} style={{ transform: f.drift.direction === 'shortening' ? 'scaleY(-1)' : 'none' }} />
                <div style={{ fontSize: 15, fontWeight: 600, color: TEXT }}>
                  Awake stretches are {f.drift.direction}
                </div>
              </div>
              <div style={{ fontSize: 13.5, color: TEXT2, lineHeight: 1.55 }}>
                Over the last week {name} has been awake about {formatDuration(Math.abs(f.drift.changeMinutes) * 60000)}
                {' '}{f.drift.direction === 'lengthening' ? 'longer' : 'less'} between sleeps than the week before
                ({f.drift.priorMedian} min then, {f.drift.recentMedian} min now).
              </div>
            </Card>
          </>
        )}

        <div style={{ fontSize: 11.5, color: TEXT2, lineHeight: 1.5, padding: '2px 4px' }}>
          This describes patterns in what you've logged. It isn't medical advice. If you have questions or
          concerns about {name}'s sleep, speak to your doctor or pediatrician.
        </div>
      </div>
    );
  };

  const SettingsTab = () => (
    <div style={{ padding: '32px 20px 24px' }}>
      <h2 style={{ margin: '0 0 24px', fontSize: 26, fontWeight: 700, color: TEXT, letterSpacing: -0.5 }}>Settings</h2>

      {/* Baby info */}
      <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.6 }}>Baby Profile</p>
      <div style={{ background: CARD, borderRadius: 16, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Name</label>
            <input type="text" placeholder="e.g. Liam" value={babyName} onChange={(e) => setBabyName(e.target.value)}
              style={{ width: '100%', height: 44, padding: '0 12px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 16, fontWeight: 600, outline: 'none', boxSizing: 'border-box', color: TEXT, background: CARD }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Gender</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['boy', 'girl'].map(g => (
                <button key={g} onClick={() => setBabyGender(g)} style={{
                  flex: 1, height: 44, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: 'pointer', background: babyGender === g ? ACCENT : BG, color: babyGender === g ? ACCENT_TEXT : TEXT2,
                }}>{g.charAt(0).toUpperCase() + g.slice(1)}</button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5 }}>Date of Birth</label>
            {babyDOB && (
              <span style={{ fontSize: 13, fontWeight: 700, color: ACCENT_TEXT }}>
                {(() => {
                  const months = Math.floor(effectiveAge / 4.345);
                  const remWeeks = effectiveAge - Math.round(months * 4.345);
                  if (months === 0) return `${effectiveAge}w old`;
                  return remWeeks > 0 ? `${months}m ${remWeeks}w old` : `${months}m old`;
                })()}
              </span>
            )}
          </div>
          <div style={{ position: 'relative', height: 44, cursor: 'pointer' }} onClick={() => {
            if (dobInputRef.current?.showPicker) {
              try { dobInputRef.current.showPicker(); } catch { dobInputRef.current.focus(); }
            } else {
              dobInputRef.current?.focus();
            }
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, border: `1.5px solid ${BORDER}`, borderRadius: 10, background: CARD, display: 'flex', alignItems: 'center', paddingLeft: 12, fontSize: 15, fontWeight: 600, color: babyDOB ? TEXT : TEXT2, pointerEvents: 'none' }}>
              {babyDOB ? new Date(babyDOB + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Tap to set date of birth'}
            </div>
            <input ref={dobInputRef} type="date" value={babyDOB} onChange={(e) => setBabyDOB(e.target.value)}
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', fontSize: 16, border: 'none', background: 'transparent' }} />
          </div>
          {!babyDOB && (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Age (weeks)</label>
              <input type="number" value={babyAge || ''} onChange={(e) => setBabyAge(parseInt(e.target.value) || 0)}
                style={{ width: '100%', height: 44, padding: '0 12px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 16, fontWeight: 600, outline: 'none', boxSizing: 'border-box', color: TEXT, background: CARD }} />
            </div>
          )}
        </div>
      </div>

      {/* Units */}
      <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.6 }}>Units</p>
      <div style={{ background: CARD, borderRadius: 16, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', gap: 10 }}>
        {['ml', 'oz'].map((u) => (
          <button key={u} onClick={() => setUnit(u)} style={{
            flex: 1, padding: '12px', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
            cursor: 'pointer', background: unit === u ? ACCENT : BG, color: unit === u ? ACCENT_TEXT : TEXT2,
          }}>{u}</button>
        ))}
      </div>

      {/* Daily goal */}
      <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.6 }}>Daily feed goal</p>
      <div style={{ background: CARD, borderRadius: 16, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ position: 'relative' }}>
          <input
            type="number"
            inputMode="decimal"
            value={dailyGoalInput}
            placeholder={`${convert(recommendedDaily)} (estimated)`}
            onFocus={() => { goalEditing.current = true; }}
            onBlur={() => { goalEditing.current = false; }}
            onChange={(e) => {
              const raw = e.target.value;
              setDailyGoalInput(raw);
              const n = parseFloat(raw);
              setDailyGoal(raw === '' || isNaN(n) || n <= 0 ? null : (unit === 'oz' ? ozToMl(n) : Math.round(n)));
            }}
            style={{ width: '100%', height: 44, padding: '0 44px 0 12px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 16, fontWeight: 600, outline: 'none', boxSizing: 'border-box', color: TEXT, background: CARD }}
          />
          <span style={{ position: 'absolute', right: 12, top: 0, height: 44, display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 600, color: TEXT2, pointerEvents: 'none' }}>{unit}</span>
        </div>
        <div style={{ fontSize: 12, color: TEXT2, marginTop: 10, lineHeight: 1.5 }}>
          {isGoalCustom
            ? 'Used on the Home and Stats screens. Clear this field to go back to the estimate.'
            : !dailyIntake
              ? "Add your baby's date of birth above to see an estimate."
              : dailyIntake.basis === 'weight'
                ? `Leave blank to use the estimate from ${dailyIntake.kg.toFixed(2)}kg (${convert(recommendedDaily)}${unit}, about ${ML_PER_KG_DAY}ml per kg per day).`
                : `Leave blank to use the estimate for a typical ${effectiveAge}-week-old (${convert(recommendedDaily)}${unit}). Log a weight in Growth to base it on your baby.`}
          {' '}If your pediatrician gave you a target, enter it here — the estimate is a rough guide, not medical advice.
        </div>
      </div>

      {/* Appearance */}
      <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.6 }}>Appearance</p>
      <div style={{ background: CARD, borderRadius: 16, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 13, color: TEXT2, marginBottom: 10 }}>Theme</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { value: 'system', label: 'System' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
          ].map(({ value, label }) => (
            <button key={value} onClick={() => { setTheme(value); localStorage.setItem('theme', value); }} style={{
              flex: 1, padding: '10px 4px', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
              cursor: 'pointer',
              background: theme === value ? ACCENT : BG,
              color: theme === value ? ACCENT_TEXT : TEXT2,
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Invite code */}
      <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.6 }}>Invite Code</p>
      <div style={{ background: CARD, borderRadius: 16, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 13, color: TEXT2, marginBottom: 4 }}>Share this code with your partner</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: ACCENT_TEXT, letterSpacing: 3 }}>{roomCode}</div>
        </div>
        <button onClick={() => navigator.clipboard?.writeText(roomCode)} style={{ background: ACCENT_BG, border: 'none', padding: '8px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: ACCENT_TEXT }}>
          Copy
        </button>
      </div>

      {/* Install app */}
      {!isStandalone && <>
        <p style={{ margin: '20px 0 10px', fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.6 }}>Install App</p>
        <div style={{ background: CARD, borderRadius: 16, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 13, color: TEXT2, marginBottom: 14 }}>
            Add TeamBaby to your home screen for the best experience — it opens like a regular app with no browser bar.
          </div>
          {installable ? (
            <button
              onClick={async () => {
                deferredInstallPrompt.current.prompt();
                const { outcome } = await deferredInstallPrompt.current.userChoice;
                if (outcome === 'accepted') { deferredInstallPrompt.current = null; setInstallable(false); }
              }}
              style={{ width: '100%', padding: '14px', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', background: ACCENT, color: ACCENT_TEXT }}
            >
              Add to Home Screen
            </button>
          ) : (
            <button
              onClick={() => setShowInstallInstructions(v => !v)}
              style={{ width: '100%', padding: '14px', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', background: ACCENT, color: ACCENT_TEXT }}
            >
              How to Install
            </button>
          )}
          {showInstallInstructions && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {isIOS ? (
                [
                  { step: '1', text: 'Tap the Share button (□↑) at the bottom of Safari' },
                  { step: '2', text: 'Scroll down and tap "Add to Home Screen"' },
                  { step: '3', text: 'Tap "Add" in the top right corner' },
                ].map(({ step, text }) => (
                  <div key={step} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: ACCENT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: ACCENT_TEXT }}>{step}</span>
                    </div>
                    <span style={{ fontSize: 14, color: TEXT, paddingTop: 4 }}>{text}</span>
                  </div>
                ))
              ) : (
                [
                  { step: '1', text: 'Tap the three-dot menu (⋮) in Chrome' },
                  { step: '2', text: 'Tap "Add to Home screen"' },
                  { step: '3', text: 'Tap "Add" to confirm' },
                ].map(({ step, text }) => (
                  <div key={step} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: ACCENT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: ACCENT_TEXT }}>{step}</span>
                    </div>
                    <span style={{ fontSize: 14, color: TEXT, paddingTop: 4 }}>{text}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </>}

      {/* Version */}
      <div style={{ marginTop: 32, textAlign: 'center' }}>
        <a href="/changelog.html" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: TEXT2, textDecoration: 'underline', textDecorationColor: BORDER }}>
          Changelog · v0.{process.env.REACT_APP_VERSION || 'dev'}
        </a>
      </div>
    </div>
  );

  // ── Stats Tab ────────────────────────────────────────────
  const StatsTab = () => {
    const todayKey = new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const isToday = statsDateKey === todayKey;
    const dayWord = isToday ? 'today' : 'that day';

    const allTimestamps = [
      ...feeds.map(f => f.timestamp), ...wakeWindows.map(w => w.start), ...diapers.map(d => d.timestamp),
      ...pumps.map(p => p.timestamp), ...weights.map(w => w.timestamp), ...medicines.map(m => m.timestamp),
    ];
    const earliestKey = allTimestamps.length > 0
      ? dateKeyOf(allTimestamps.reduce((min, ts) => new Date(ts) < new Date(min) ? ts : min))
      : todayKey;
    const atEarliest = new Date(statsDateKey) <= new Date(earliestKey);

    const statsDateLabel = (() => {
      if (isToday) return 'Today';
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayKey = yesterday.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
      if (statsDateKey === yesterdayKey) return 'Yesterday';
      return new Date(statsDateKey).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    })();

    const dayFeeds = getFeedsForDate(statsDateKey);
    // Latch feeds have no volume, so ml totals and averages count bottles only.
    const dayBottleFeeds = dayFeeds.filter((f) => !f.latch);
    const dayLatchFeeds = dayFeeds.filter((f) => f.latch);
    const dayLatchMs = dayLatchFeeds.reduce((sum, f) => sum + (f.leftMs || 0) + (f.rightMs || 0), 0);
    const dayTotal = dayBottleFeeds.reduce((sum, f) => sum + f.amount, 0);
    const dayProgressFrac = Math.min(dayTotal / effectiveDailyGoal, 1);
    const dayProgressOffset = PCIRC * (1 - dayProgressFrac);

    const todayWW = getWakeWindowsForDate(statsDateKey);
    const totalAwakeMs = todayWW.reduce((sum, w) => sum + (new Date(w.end) - new Date(w.start)), 0);
    const { max } = getRecommendedWakeWindow();
    const todayNaps = getNapsForDate(statsDateKey);
    const totalNapMs = todayNaps.reduce((sum, n) => sum + (new Date(n.end) - new Date(n.start)), 0);
    // Naps and wake windows share the {start,end} shape; tag each and interleave
    // them into one newest-first timeline for the Sleep section.
    const sleepTimeline = [
      ...todayNaps.map(n => ({ kind: 'nap', entry: n })),
      ...todayWW.map(w => ({ kind: 'sleep', entry: w })),
    ].sort((a, b) => new Date(b.entry.start) - new Date(a.entry.start));
    const todayD = getDiapersForDate(statsDateKey);
    const wetCount = todayD.filter(d => d.type === 'wet' || d.type === 'both').length;
    const dirtyCount = todayD.filter(d => d.type === 'dirty' || d.type === 'both').length;
    const todayP = getPumpsForDate(statsDateKey);
    const pumpTotal = todayP.reduce((sum, p) => sum + p.amount, 0);
    const avgPump = todayP.length > 0 ? Math.round(pumpTotal / todayP.length) : 0;


    const SectionHeader = ({ label, show, onToggle, Icon, iconColor, iconBg }) => (
      <div style={{ background: CARD, borderRadius: 16, padding: '16px 20px', marginBottom: show ? 12 : 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <button onClick={onToggle} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={20} color={iconColor} />
            </div>
            <span style={{ fontSize: 16, fontWeight: 600, color: TEXT }}>{label}</span>
          </div>
          {show ? <ChevronUp size={22} color={TEXT2} /> : <ChevronDown size={22} color={TEXT2} />}
        </button>
      </div>
    );

    return (
    <div style={{ padding: '32px 20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: TEXT, letterSpacing: -0.5 }}>{babyName ? `${babyName}'s Summary` : 'Summary'}</h2>
        <button
          onClick={() => {
            const allOpen = showFeeds && showSleep && showDiapers && showPumping && showWeights && showMedicines;
            setShowFeeds(!allOpen); setShowSleep(!allOpen); setShowDiapers(!allOpen); setShowPumping(!allOpen); setShowWeights(!allOpen); setShowMedicines(!allOpen);
          }}
          style={{ background: 'none', border: `1.5px solid ${BORDER}`, borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 600, color: TEXT2, cursor: 'pointer' }}
        >
          {showFeeds && showSleep && showDiapers && showPumping && showWeights && showMedicines ? 'Collapse All' : 'Expand All'}
        </button>
      </div>

      {/* ── Date navigator ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: CARD, borderRadius: 16, padding: '10px 14px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <button
          onClick={() => shiftStatsDate(-1)}
          disabled={atEarliest}
          style={{ background: 'none', border: 'none', cursor: atEarliest ? 'default' : 'pointer', padding: 8, color: atEarliest ? BORDER : TEXT, display: 'flex', alignItems: 'center' }}
        >
          <ChevronLeft size={22} />
        </button>
        <span style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>{statsDateLabel}</span>
        <button
          onClick={() => shiftStatsDate(1)}
          disabled={isToday}
          style={{ background: 'none', border: 'none', cursor: isToday ? 'default' : 'pointer', padding: 8, color: isToday ? BORDER : TEXT, display: 'flex', alignItems: 'center' }}
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* ── Feeds section ── */}
      <SectionHeader label="Feeds" show={showFeeds} onToggle={() => setShowFeeds(v => !v)} Icon={Milk} iconColor={ACCENT_TEXT} iconBg={FEED_BG} />

      {showFeeds && <>
      {/* Progress ring card */}
      <div style={{ background: CARD, borderRadius: 16, padding: '18px 20px', marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ position: 'relative', width: pSize, height: pSize, flexShrink: 0 }}>
          <svg width={pSize} height={pSize} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={pSize / 2} cy={pSize / 2} r={PR} fill="none" stroke={BORDER} strokeWidth={10} />
            <circle
              cx={pSize / 2} cy={pSize / 2} r={PR}
              fill="none" stroke={dayProgressFrac >= 1 ? GREEN : ACCENT_TEXT} strokeWidth={10}
              strokeLinecap="round"
              strokeDasharray={PCIRC}
              strokeDashoffset={dayProgressOffset}
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: dayProgressFrac >= 1 ? GREEN : TEXT }}>
              {dayBottleFeeds.length > 0 ? `${Math.round(dayProgressFrac * 100)}%` : '—'}
            </span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: TEXT, letterSpacing: -0.5, marginBottom: 3 }}>
            {dayFeeds.length === 0
              ? '—'
              : dayBottleFeeds.length > 0 ? `${convert(dayTotal)}${unit}` : formatLatchMs(dayLatchMs)}
          </div>
          {/* Same rule as Home: no ml goal when nothing was bottle-fed that day. */}
          <div style={{ fontSize: 13, color: TEXT2, marginBottom: 8 }}>
            {dayBottleFeeds.length > 0 ? (
              <>
                of {convert(effectiveDailyGoal)}{unit} daily goal
                {!isGoalCustom && (
                  <span style={{ fontSize: 11, color: TEXT2, marginLeft: 4 }}>
                    ({weights.length > 0 ? 'by weight' : 'by age'})
                  </span>
                )}
              </>
            ) : dayLatchFeeds.length > 0 ? 'at the breast' : 'no feeds logged'}
          </div>
          <div style={{ fontSize: 13, color: TEXT2 }}>
            {dayFeeds.length} feed{dayFeeds.length !== 1 ? 's' : ''}
            {dayLatchFeeds.length > 0 && ` · ${dayLatchFeeds.length} latch (${formatLatchMs(dayLatchMs)})`}
          </div>
        </div>
      </div>

      {/* Feed stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div style={{ background: CARD, borderRadius: 14, padding: '14px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: TEXT, letterSpacing: -0.5, marginBottom: 3 }}>
            {dayBottleFeeds.length > 0 ? `${convert(Math.round(dayTotal / dayBottleFeeds.length))}${unit}` : '—'}
          </div>
          <div style={{ fontSize: 12, color: TEXT2, fontWeight: 500 }}>Avg per bottle</div>
        </div>
        <div style={{ background: CARD, borderRadius: 14, padding: '14px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: dayProgressFrac >= 1 ? GREEN : TEXT, letterSpacing: -0.5, marginBottom: 3 }}>
            {dayBottleFeeds.length === 0
              ? '—'
              : dayProgressFrac >= 1 ? '✓ Done' : `${convert(Math.max(effectiveDailyGoal - dayTotal, 0))}${unit}`}
          </div>
          <div style={{ fontSize: 12, color: TEXT2, fontWeight: 500 }}>Remaining</div>
        </div>
      </div>

      {/* Feed list */}
      {dayFeeds.length > 0 ? (
        <div style={{ background: CARD, borderRadius: 16, padding: '18px 20px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {[...dayFeeds].reverse().map((feed, idx) => (
            <div key={idx} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 0',
              borderBottom: idx < dayFeeds.length - 1 ? `1px solid ${BORDER}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Droplet size={16} color={ACCENT_TEXT} />
                </div>
                <span style={{ fontSize: 15, fontWeight: 500, color: TEXT }}>
                  {new Date(feed.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: ACCENT_TEXT }}>
                  {feed.latch ? latchLabel(feed).total : `${convert(feed.amount)}${unit}`}
                </span>
                <button onClick={() => setEntryActionSheet({ type: 'feed', entry: feed })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: TEXT2, display: 'flex', alignItems: 'center', borderRadius: 6 }}>
                  <MoreHorizontal size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: CARD, borderRadius: 16, padding: '24px 20px', textAlign: 'center', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <p style={{ margin: 0, fontSize: 14, color: TEXT2 }}>No feeds logged {dayWord} yet.</p>
        </div>
      )}

      </>}

      {/* ── Sleep section ── */}
      <SectionHeader label="Sleep & Wake" show={showSleep} onToggle={() => setShowSleep(v => !v)} Icon={SunMoon} iconColor={ACCENT_TEXT} iconBg={FEED_BG} />

      {showSleep && <>
      {/* Sleep stats grid: naps (actual sleep) and awake windows side by side. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div style={{ background: CARD, borderRadius: 14, padding: '12px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, letterSpacing: -0.5, marginBottom: 3 }}>{todayNaps.length}</div>
          <div style={{ fontSize: 11, color: TEXT2, fontWeight: 500 }}>Naps{todayNaps.length > 0 ? ` · ${formatDuration(totalNapMs)}` : ''}</div>
        </div>
        <div style={{ background: CARD, borderRadius: 14, padding: '12px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, letterSpacing: -0.5, marginBottom: 3 }}>{todayWW.length}</div>
          <div style={{ fontSize: 11, color: TEXT2, fontWeight: 500 }}>Awake{todayWW.length > 0 ? ` · ${formatDuration(totalAwakeMs)}` : ''}</div>
        </div>
      </div>

      {/* Sleep timeline: naps and awake windows interleaved, newest first. */}
      {sleepTimeline.length > 0 ? (
        <div style={{ background: CARD, borderRadius: 16, padding: '18px 20px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {sleepTimeline.map(({ kind, entry: w }, idx, arr) => {
            const duration = new Date(w.end) - new Date(w.start);
            const isNap = kind === 'nap';
            const onTrack = duration / 60000 <= max;
            return (
              <div key={`${kind}-${w.start}`} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '11px 0',
                borderBottom: idx < arr.length - 1 ? `1px solid ${BORDER}` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: isNap ? FEED_BG : BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {isNap ? <Moon size={16} color={ACCENT_TEXT} /> : <Sun size={16} color={ACCENT_TEXT} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>
                      {isNap ? 'Nap' : 'Awake'} · {formatDuration(duration)}
                    </div>
                    <div style={{ fontSize: 12, color: TEXT2, marginTop: 1 }}>
                      {new Date(w.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} – {new Date(w.end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {!isNap && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
                      background: onTrack ? PUMP_BG : OVER_BG,
                      color: onTrack ? GREEN : RED,
                    }}>
                      {onTrack ? 'On track' : 'Over window'}
                    </span>
                  )}
                  <button onClick={() => setEntryActionSheet({ type: kind, entry: w })}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: TEXT2, display: 'flex', alignItems: 'center', borderRadius: 6 }}>
                    <MoreHorizontal size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: CARD, borderRadius: 16, padding: '24px 20px', textAlign: 'center', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <p style={{ margin: 0, fontSize: 14, color: TEXT2 }}>No sleep logged {dayWord} yet.</p>
        </div>
      )}

      </>}

      {/* ── Diapers section ── */}
      <SectionHeader label="Diapers" show={showDiapers} onToggle={() => setShowDiapers(v => !v)} Icon={Wind} iconColor={AMBER} iconBg={DIAPER_BG} />

      {showDiapers && <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
        {[
          { value: todayD.length || '—', label: 'Total' },
          { value: todayD.length > 0 ? wetCount : '—', label: 'Wet' },
          { value: todayD.length > 0 ? dirtyCount : '—', label: 'Dirty' },
        ].map(({ value, label }) => (
          <div key={label} style={{ background: CARD, borderRadius: 14, padding: '12px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, letterSpacing: -0.5, marginBottom: 3 }}>{value}</div>
            <div style={{ fontSize: 11, color: TEXT2, fontWeight: 500 }}>{label}</div>
          </div>
        ))}
      </div>

      {todayD.length > 0 ? (
        <div style={{ background: CARD, borderRadius: 16, padding: '18px 20px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {[...todayD].reverse().map((d, idx) => (
            <div key={idx} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 0', borderBottom: idx < todayD.length - 1 ? `1px solid ${BORDER}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: DIAPER_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Wind size={16} color={AMBER} />
                </div>
                <span style={{ fontSize: 15, fontWeight: 500, color: TEXT }}>
                  {new Date(d.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: TEXT2, textTransform: 'capitalize' }}>{d.type}</span>
                  {d.color && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', marginTop: 2 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: { yellow:'#F5C842', mustard:'#C8A415', brown:'#8B5E3C', green:'#4CAF50', orange:'#FF9500', black:'#2C2C2C', red:'#FF3B30', white:'#E5E5EA' }[d.color] || TEXT2, border: d.color === 'white' ? `1px solid ${BORDER}` : 'none' }} />
                      <span style={{ fontSize: 11, color: TEXT2, textTransform: 'capitalize' }}>{d.color}</span>
                    </div>
                  )}
                </div>
                <button onClick={() => setEntryActionSheet({ type: 'diaper', entry: d })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: TEXT2, display: 'flex', alignItems: 'center', borderRadius: 6 }}>
                  <MoreHorizontal size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: CARD, borderRadius: 16, padding: '24px 20px', textAlign: 'center', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <p style={{ margin: 0, fontSize: 14, color: TEXT2 }}>No diapers logged {dayWord} yet.</p>
        </div>
      )}

      </>}

      {/* ── Pumping section ── */}
      <SectionHeader label="Pumping" show={showPumping} onToggle={() => setShowPumping(v => !v)} Icon={Activity} iconColor={GREEN} iconBg={PUMP_BG} />

      {showPumping && <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
        {[
          { value: todayP.length || '—', label: 'Sessions' },
          { value: todayP.length > 0 ? `${convert(pumpTotal)}${unit}` : '—', label: 'Total' },
          { value: todayP.length > 0 ? `${convert(avgPump)}${unit}` : '—', label: 'Avg' },
        ].map(({ value, label }) => (
          <div key={label} style={{ background: CARD, borderRadius: 14, padding: '12px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, letterSpacing: -0.5, marginBottom: 3 }}>{value}</div>
            <div style={{ fontSize: 11, color: TEXT2, fontWeight: 500 }}>{label}</div>
          </div>
        ))}
      </div>

      {todayP.length > 0 ? (
        <div style={{ background: CARD, borderRadius: 16, padding: '18px 20px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {[...todayP].reverse().map((p, idx) => (
            <div key={idx} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 0', borderBottom: idx < todayP.length - 1 ? `1px solid ${BORDER}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: PUMP_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Activity size={16} color={GREEN} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: TEXT }}>
                    {new Date(p.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </div>
                  {(p.left != null || p.right != null) && (
                    <div style={{ fontSize: 11, color: TEXT2, marginTop: 2 }}>
                      L {convert(p.left || 0)}{unit} · R {convert(p.right || 0)}{unit}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: GREEN }}>{convert(p.amount)}{unit}</span>
                <button onClick={() => setEntryActionSheet({ type: 'pump', entry: p })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: TEXT2, display: 'flex', alignItems: 'center', borderRadius: 6 }}>
                  <MoreHorizontal size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: CARD, borderRadius: 16, padding: '24px 20px', textAlign: 'center', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <p style={{ margin: 0, fontSize: 14, color: TEXT2 }}>No pumping sessions logged {dayWord} yet.</p>
        </div>
      )}
      </>}

      {/* ── Weight section ── */}
      <SectionHeader label="Weight" show={showWeights} onToggle={() => setShowWeights(v => !v)} Icon={Scale} iconColor={WEIGHT_COLOR} iconBg={WEIGHT_BG} />

      {showWeights && (() => {
        const todayW = getWeightsForDate(statsDateKey);
        const latestWeight = weights.length > 0 ? weights[weights.length - 1] : null;
        return (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              {[
                { value: todayW.length > 0 ? `${(todayW[todayW.length - 1].grams / 1000).toFixed(2)}kg` : '—', label: isToday ? "Today's weight" : "That day's weight" },
                { value: latestWeight ? `${(latestWeight.grams / 1000).toFixed(2)}kg` : '—', label: 'Last recorded' },
              ].map(({ value, label }) => (
                <div key={label} style={{ background: CARD, borderRadius: 14, padding: '12px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: WEIGHT_COLOR, letterSpacing: -0.5, marginBottom: 3 }}>{value}</div>
                  <div style={{ fontSize: 11, color: TEXT2, fontWeight: 500 }}>{label}</div>
                </div>
              ))}
            </div>
            {todayW.length > 0 ? (
              <div style={{ background: CARD, borderRadius: 16, padding: '18px 20px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                {[...todayW].reverse().map((w, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: idx < todayW.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: WEIGHT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Scale size={16} color={WEIGHT_COLOR} />
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 500, color: TEXT }}>{new Date(w.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: WEIGHT_COLOR }}>{(w.grams / 1000).toFixed(2)}kg</span>
                      <button onClick={() => setEntryActionSheet({ type: 'weight', entry: w })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: TEXT2, display: 'flex', alignItems: 'center', borderRadius: 6 }}>
                        <MoreHorizontal size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ background: CARD, borderRadius: 16, padding: '24px 20px', textAlign: 'center', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <p style={{ margin: 0, fontSize: 14, color: TEXT2 }}>No weight logged {dayWord} yet.</p>
              </div>
            )}
          </>
        );
      })()}

      {/* ── Medicine section ── */}
      <SectionHeader label="Medicine" show={showMedicines} onToggle={() => setShowMedicines(v => !v)} Icon={Pill} iconColor={MED_COLOR} iconBg={MED_BG} />

      {showMedicines && (() => {
        const todayM = getMedicinesForDate(statsDateKey);
        return (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              {[
                { value: todayM.length || '—', label: isToday ? 'Doses today' : 'Doses that day' },
                { value: medicines.length || '—', label: 'Total logged' },
              ].map(({ value, label }) => (
                <div key={label} style={{ background: CARD, borderRadius: 14, padding: '12px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: MED_COLOR, letterSpacing: -0.5, marginBottom: 3 }}>{value}</div>
                  <div style={{ fontSize: 11, color: TEXT2, fontWeight: 500 }}>{label}</div>
                </div>
              ))}
            </div>
            {todayM.length > 0 ? (
              <div style={{ background: CARD, borderRadius: 16, padding: '18px 20px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                {[...todayM].reverse().map((m, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: idx < todayM.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: MED_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Pill size={16} color={MED_COLOR} />
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>{m.name}</div>
                        {m.dose && <div style={{ fontSize: 12, color: TEXT2, marginTop: 1 }}>{m.dose}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, color: TEXT2, fontWeight: 500 }}>{new Date(m.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                      <button onClick={() => setEntryActionSheet({ type: 'medicine', entry: m })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: TEXT2, display: 'flex', alignItems: 'center', borderRadius: 6 }}>
                        <MoreHorizontal size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ background: CARD, borderRadius: 16, padding: '24px 20px', textAlign: 'center', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <p style={{ margin: 0, fontSize: 14, color: TEXT2 }}>No medicine logged {dayWord} yet.</p>
              </div>
            )}
          </>
        );
      })()}

    </div>
    );
  };

  // ── Growth Tab ───────────────────────────────────────────
  const GrowthTab = () => {

    const PCTLS = [3, 5, 10, 25, 50, 75, 90, 95, 97];
    const getPercentile = (gender, ageMonths, weightKg) => {
      const vals = interpWho(gender, ageMonths).slice(1);
      if (weightKg <= vals[0]) return '< 3rd';
      if (weightKg >= vals[8]) return '> 97th';
      for (let i = 0; i < vals.length - 1; i++) {
        if (weightKg >= vals[i] && weightKg <= vals[i + 1]) {
          const frac = (weightKg - vals[i]) / (vals[i + 1] - vals[i]);
          return Math.round(PCTLS[i] + frac * (PCTLS[i + 1] - PCTLS[i]));
        }
      }
      return null;
    };

    const ordinal = (n) => {
      if (typeof n !== 'number') return n;
      const s = ['th','st','nd','rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const toSubP = (n) => {
      if (typeof n !== 'number') {
        const digits = n.replace(/[^0-9]/g, '').split('').map(d => '₀₁₂₃₄₅₆₇₈₉'[d]).join('');
        return (n.startsWith('<') ? '<' : '>') + 'P' + digits;
      }
      const sub = String(n).split('').map(d => '₀₁₂₃₄₅₆₇₈₉'[d]).join('');
      return 'P' + sub;
    };

    const gender = babyGender === 'girl' ? 'girl' : 'boy';
    const table = WHO_WEIGHT[gender];

    // All logged weights (age may be negative if a weight predates the DOB, or
    // >36mo if off the chart). The history list shows every one of these so an
    // entry can always be seen and deleted; the chart/stat cards use the subset
    // that falls inside the WHO 0–36mo window.
    const allPoints = babyDOB ? weights.map(w => ({
      ageMonths: (new Date(w.timestamp) - new Date(babyDOB)) / (1000 * 60 * 60 * 24 * 30.4375),
      weightKg: w.grams / 1000,
      timestamp: w.timestamp,
    })).sort((a, b) => a.ageMonths - b.ageMonths) : [];
    const babyPoints = allPoints.filter(p => p.ageMonths >= 0 && p.ageMonths <= 36);

    const latest = babyPoints.length > 0 ? babyPoints[babyPoints.length - 1] : null;
    const currentPercentile = latest ? getPercentile(gender, latest.ageMonths, latest.weightKg) : null;

    const svgW = 340, svgH = 210;
    const ml = 36, mr = 10, mt = 12, mb = 28;
    const cW = svgW - ml - mr, cH = svgH - mt - mb;
    const minWt = 1.5, maxWt = 20, maxAge = 36;
    const xOf = (m) => ml + (m / maxAge) * cW;
    const yOf = (kg) => mt + (1 - (kg - minWt) / (maxWt - minWt)) * cH;
    const cdcLine = (col) => table.map(row => `${xOf(row[0]).toFixed(1)},${yOf(row[col]).toFixed(1)}`).join(' ');
    const babyLine = babyPoints.map(p => `${xOf(p.ageMonths).toFixed(1)},${yOf(p.weightKg).toFixed(1)}`).join(' ');

    const yGridWeights = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20];
    const xGridMonths = [0, 6, 12, 18, 24, 30, 36];
    const pLines = [
      [1, 0.18], [2, 0.25], [3, 0.32], [4, 0.42],
      [5, 0.58], [6, 0.42], [7, 0.32], [8, 0.25], [9, 0.18],
    ];
    const legendItems = [
      { label: 'P5', opacity: 0.25 }, { label: 'P25', opacity: 0.42 },
      { label: 'P50', opacity: 0.58 }, { label: 'P75', opacity: 0.42 }, { label: 'P95', opacity: 0.25 },
    ];

    if (!babyDOB) return (
      <div style={{ padding: '32px 20px 24px' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 700, color: TEXT, letterSpacing: -0.5 }}>Growth</h2>
        <div style={{ background: CARD, borderRadius: 16, padding: '36px 20px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginTop: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📏</div>
          <div style={{ fontSize: 17, fontWeight: 600, color: TEXT, marginBottom: 8 }}>Add your baby's date of birth</div>
          <div style={{ fontSize: 14, color: TEXT2, marginBottom: 24, lineHeight: 1.5 }}>Set your baby's birthday in Settings to see WHO growth percentile charts.</div>
          <button onClick={() => setActiveTab('settings')} style={{ background: ACCENT, color: ACCENT_TEXT, border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            Go to Settings
          </button>
        </div>
      </div>
    );

    return (
      <div style={{ padding: '32px 20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: TEXT, letterSpacing: -0.5 }}>Growth</h2>
          <span style={{ fontSize: 11, color: TEXT2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>WHO · 0–36 mo</span>
        </div>

        {latest && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            <div style={{ background: CARD, borderRadius: 14, padding: '14px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: WEIGHT_COLOR, marginBottom: 3 }}>
                {latest.weightKg >= 1 ? `${latest.weightKg.toFixed(2)} kg` : `${Math.round(latest.weightKg * 1000)} g`}
              </div>
              <div style={{ fontSize: 11, color: TEXT2, fontWeight: 500 }}>Latest weight</div>
            </div>
            <div style={{ background: CARD, borderRadius: 14, padding: '14px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: ACCENT_TEXT, marginBottom: 3 }}>
                {ordinal(currentPercentile)}
              </div>
              <div style={{ fontSize: 11, color: TEXT2, fontWeight: 500 }}>Weight percentile</div>
            </div>
          </div>
        )}

        <div style={{ background: CARD, borderRadius: 16, padding: '16px 10px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 16 }}>
          <svg viewBox={`0 0 ${svgW} ${svgH}`} style={{ width: '100%', display: 'block', overflow: 'visible' }}>
            {yGridWeights.filter(w => w >= minWt).map(w => (
              <g key={w}>
                <line x1={ml} y1={yOf(w)} x2={ml + cW} y2={yOf(w)} stroke={BORDER} strokeWidth={0.5} />
                <text x={ml - 4} y={yOf(w) + 3.5} textAnchor="end" fontSize={8.5} fill={TEXT2}>{w}</text>
              </g>
            ))}
            {xGridMonths.map(m => (
              <g key={m}>
                <line x1={xOf(m)} y1={mt} x2={xOf(m)} y2={mt + cH} stroke={BORDER} strokeWidth={0.5} />
                <text x={xOf(m)} y={mt + cH + 14} textAnchor="middle" fontSize={8.5} fill={TEXT2}>{m}m</text>
              </g>
            ))}
            {pLines.map(([col, opacity]) => (
              <polyline key={col} points={cdcLine(col)} fill="none" stroke={WEIGHT_COLOR}
                strokeWidth={col === 5 ? 1.5 : 1} strokeOpacity={opacity} />
            ))}
            {babyPoints.length > 1 && (
              <polyline points={babyLine} fill="none" stroke={ACCENT_TEXT}
                strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
            )}
            {babyPoints.map((p, i) => (
              <circle key={i} cx={xOf(p.ageMonths)} cy={yOf(p.weightKg)} r={4.5}
                fill={ACCENT_TEXT} stroke={CARD} strokeWidth={2} />
            ))}
          </svg>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            {legendItems.map(({ label, opacity }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 14, height: 2, background: WEIGHT_COLOR, opacity, borderRadius: 1 }} />
                <span style={{ fontSize: 10, color: TEXT2 }}>{label}</span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 14, height: 3, background: ACCENT_TEXT, borderRadius: 1 }} />
              <span style={{ fontSize: 10, color: TEXT2 }}>{babyName || 'Baby'}</span>
            </div>
          </div>
        </div>

        {allPoints.length === 0 ? (
          <div style={{ background: CARD, borderRadius: 16, padding: '24px 20px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <p style={{ margin: 0, fontSize: 14, color: TEXT2 }}>No weight entries yet. Log weight from the home screen.</p>
          </div>
        ) : (
          <div style={{ background: CARD, borderRadius: 16, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Weight History</div>
            {[...allPoints].reverse().slice(0, 15).map((p, idx, arr) => {
              const inRange = p.ageMonths >= 0 && p.ageMonths <= 36;
              const pct = inRange ? getPercentile(gender, p.ageMonths, p.weightKg) : null;
              const totalWeeks = Math.floor(p.ageMonths * 4.345);
              const mos = Math.floor(totalWeeks / 4.345);
              const remWeeks = totalWeeks - Math.round(mos * 4.345);
              const ageLabel = p.ageMonths < 0 ? 'before DOB' : mos === 0 ? `${totalWeeks}w` : remWeeks > 0 ? `${mos}m ${remWeeks}w` : `${mos}m`;
              return (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: idx < arr.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: WEIGHT_COLOR }}>
                      {p.weightKg >= 1 ? `${p.weightKg.toFixed(2)} kg` : `${Math.round(p.weightKg * 1000)} g`}
                    </div>
                    <div style={{ fontSize: 11, color: TEXT2, marginTop: 1 }}>
                      {p.ageMonths < 0 ? ageLabel : `${ageLabel} old`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: ACCENT_TEXT }}>
                        {pct != null ? toSubP(pct) : '—'}
                      </div>
                      <div style={{ fontSize: 11, color: TEXT2, marginTop: 1 }}>
                        {new Date(p.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                    <button onClick={() => openEditEntry('weight', weights.find(w => w.timestamp === p.timestamp))}
                      aria-label="Edit weight entry"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: TEXT2, display: 'flex', alignItems: 'center', borderRadius: 8 }}>
                      <Pencil size={16} />
                    </button>
                    {confirmDeleteWeight === p.timestamp ? (
                      <button onClick={() => { deleteEntry('weight', weights.find(w => w.timestamp === p.timestamp)); setConfirmDeleteWeight(null); }}
                        aria-label="Confirm delete weight entry"
                        style={{ background: RED, border: 'none', cursor: 'pointer', padding: '6px 10px', color: 'white', display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
                        <Trash2 size={14} /> Delete?
                      </button>
                    ) : (
                      <button onClick={() => { setConfirmDeleteWeight(p.timestamp); setTimeout(() => setConfirmDeleteWeight(cur => cur === p.timestamp ? null : cur), 5000); }}
                        aria-label="Delete weight entry"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: RED, display: 'flex', alignItems: 'center', borderRadius: 8 }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (splashVisible) return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: ACCENT,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      opacity: splashFading ? 0 : 1,
      transition: 'opacity 0.55s ease',
    }}>
      <style>{`
        @keyframes splashPop {
          0%   { opacity: 0; transform: scale(0.82); }
          60%  { opacity: 1; transform: scale(1.04); }
          100% { opacity: 1; transform: scale(1); }
        }
        .splash-inner { animation: splashPop 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        @keyframes splashTagline {
          0%   { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .splash-tagline { opacity: 0; animation: splashTagline 0.4s ease 0.45s forwards; }
      `}</style>
      <div className="splash-inner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg width="140" height="140" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 20 }}>
          <path d="M 52 13 L 30 18 Q 17 21 17 34 L 17 56 Q 20 70 50 82 Q 80 70 83 56 L 83 34 Q 83 24 71 20 L 64 16"
            fill="none" stroke="white" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="50" cy="57" r="19" fill="white"/>
          <path d="M 46 38 Q 50 33 54 38" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"/>
          <path d="M 42 53 Q 44.5 50 47 53" fill="none" stroke="#6BAFC5" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M 53 53 Q 55.5 50 58 53" fill="none" stroke="#6BAFC5" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M 43 62 Q 50 68 57 62" fill="none" stroke="#6BAFC5" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M 55 22 L 56.5 26.5 L 61 28 L 56.5 29.5 L 55 34 L 53.5 29.5 L 49 28 L 53.5 26.5 Z" fill="white"/>
          <path d="M 75 10 L 77.5 17.5 L 85 20 L 77.5 22.5 L 75 30 L 72.5 22.5 L 65 20 L 72.5 17.5 Z" fill="white"/>
        </svg>
        <div style={{ fontSize: 40, fontWeight: 800, color: '#FFFFFF', letterSpacing: -1, marginBottom: 6 }}>TeamBaby</div>
      </div>
      <div className="splash-tagline" style={{ fontSize: 15, color: 'rgba(255,255,255,0.82)', fontWeight: 400, letterSpacing: 0.1 }}>
        Track feeds, wake windows &amp; more
      </div>
    </div>
  );

  if (!roomCode) return (
    <div style={{ maxWidth: 430, margin: '0 auto', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 24px', background: BG }}>
      <img src="/favicon.svg" alt="TeamBaby" style={{ width: 56, height: 56, marginBottom: 12 }} />
      <h1 style={{ fontSize: 28, fontWeight: 700, color: TEXT, letterSpacing: -0.5, marginBottom: 8 }}>TeamBaby</h1>
      <p style={{ fontSize: 15, color: TEXT2, marginBottom: 40 }}>Track your baby together with your partner</p>

      <div style={{ width: '100%', background: CARD, borderRadius: 20, padding: '24px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 16 }}>
        <p style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.6 }}>New family</p>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: TEXT2, lineHeight: 1.5 }}>Set up tracking for your baby. You'll get an invite code to share with your partner.</p>
        <button onClick={createRoom} disabled={roomLoading} style={{ width: '100%', padding: '14px', background: roomLoading ? BORDER : ACCENT, color: roomLoading ? TEXT2 : ACCENT_TEXT, border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: roomLoading ? 'default' : 'pointer' }}>
          {roomLoading ? 'Setting up…' : 'Get Started'}
        </button>
        {roomError && <p style={{ margin: '10px 0 0', fontSize: 13, color: RED }}>{roomError}</p>}
      </div>

      <div style={{ width: '100%', background: CARD, borderRadius: 20, padding: '24px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
        <p style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.6 }}>Join your partner</p>
        <input
          type="text"
          placeholder="Enter invite code"
          value={roomInput}
          onChange={e => { setRoomInput(e.target.value.toUpperCase()); setRoomError(''); }}
          style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 16, fontWeight: 600, outline: 'none', boxSizing: 'border-box', color: TEXT, letterSpacing: 2, marginBottom: 12, background: CARD }}
        />
        {roomError && <p style={{ margin: '0 0 10px', fontSize: 13, color: RED }}>{roomError}</p>}
        <button onClick={joinRoom} disabled={roomLoading} style={{ width: '100%', padding: '14px', background: roomLoading ? BORDER : ACCENT_TEXT, color: 'white', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: roomLoading ? 'default' : 'pointer' }}>
          {roomLoading ? 'Joining…' : 'Join'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{
      maxWidth: 430,
      margin: '0 auto',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: BG,
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      color: TEXT,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 74 }}>
        {!isOnline && (
          <div style={{ background: AMBER, color: 'white', fontSize: 13, fontWeight: 600, textAlign: 'center', padding: '10px 16px', letterSpacing: 0.2, lineHeight: 1.4 }}>
            You're offline{fromCache ? ' — showing last saved data' : ''}. New entries won't sync until you reconnect.
          </div>
        )}
        {activeTab === 'home' && HomeTab()}
        {activeTab === 'sleep' && SleepTab()}
        {activeTab === 'stats' && StatsTab()}
        {activeTab === 'growth' && GrowthTab()}
        {activeTab === 'settings' && SettingsTab()}
      </div>

      {/* Quick Log Bottom Sheet */}
      {quickLogModal && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          onClick={() => { setQuickLogModal(null); setEditingEntry(null); setLogDate(''); setLogTime(''); }}
        >
          <div
            style={{ background: CARD, borderRadius: '20px 20px 0 0', padding: '16px 20px 40px', maxWidth: 430, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle */}
            <div style={{ width: 36, height: 4, background: BORDER, borderRadius: 2, margin: '0 auto 20px' }} />

            {/* Shared date + time picker (not shown for sleep which has its own) */}
            {quickLogModal !== 'sleep' && <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: BG, borderRadius: 10 }}>
                <span style={{ fontSize: 13, color: TEXT2, fontWeight: 500 }}>Date</span>
                <input type="date" value={logDate} onChange={e => setLogDate(e.target.value)}
                  max={localDateStr(new Date())}
                  style={{ border: 'none', background: 'none', fontSize: 15, fontWeight: 600, color: TEXT, outline: 'none', cursor: 'pointer' }} />
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: BG, borderRadius: 10 }}>
                <span style={{ fontSize: 13, color: TEXT2, fontWeight: 500 }}>Time</span>
                <input type="time" value={logTime} onChange={e => setLogTime(e.target.value)}
                  style={{ border: 'none', background: 'none', fontSize: 15, fontWeight: 600, color: TEXT, outline: 'none', cursor: 'pointer' }} />
              </div>
            </div>}

            {quickLogModal === 'feed' && (
              <>
                <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700, color: TEXT }}>{editingEntry?.type === 'feed' ? 'Edit Feed' : 'Log a Feed'}</h3>
                {/* Editing an existing entry stays on the bottle form it was logged with. */}
                {!editingEntry && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 18, background: BG, borderRadius: 12, padding: 4 }}>
                    {[{ id: 'bottle', label: 'Bottle' }, { id: 'latch', label: 'Direct latch' }].map((m) => (
                      <button key={m.id} onClick={() => setFeedMode(m.id)} style={{
                        flex: 1, padding: '10px', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                        background: feedMode === m.id ? CARD : 'transparent',
                        color: feedMode === m.id ? TEXT : TEXT2,
                        boxShadow: feedMode === m.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                      }}>{m.label}</button>
                    ))}
                  </div>
                )}
                {(feedMode === 'latch' && !editingEntry) ? (
                  LatchTimer()
                ) : (
                <>
                {/* −10/+10 steppers write into the Custom field so the amount can
                    be fine-tuned before logging; the middle tile one-taps the
                    suggested amount. Stepping starts from the current Custom value,
                    or the suggested amount when Custom is empty. */}
                {(() => {
                  // Step by a unit-appropriate amount: 10ml, or 0.5oz (~15ml).
                  const step = unit === 'oz' ? 0.5 : 10;
                  const stepAmount = (delta) => {
                    const cur = parseFloat(customAmount);
                    const base = isNaN(cur) ? convert(recommended) : cur;
                    const next = Math.max(0, Math.round((base + delta) * 10) / 10);
                    setCustomAmount(String(next));
                  };
                  return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 8 }}>
                  <button onClick={() => stepAmount(-step)} style={{
                    padding: '18px 8px', cursor: 'pointer', border: `1.5px solid ${BORDER}`,
                    borderRadius: 14, fontSize: 17, fontWeight: 700, background: CARD, color: TEXT,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}>
                    −{step}{unit}
                    <span style={{ fontSize: 11, fontWeight: 500, color: TEXT2 }}>Adjust</span>
                  </button>
                  <button onClick={() => { logFeed(recommended); setQuickLogModal(null); }} style={{
                    padding: '18px 8px', cursor: 'pointer', border: 'none',
                    borderRadius: 14, fontSize: 17, fontWeight: 700, background: ACCENT, color: ACCENT_TEXT,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}>
                    {convert(recommended)}{unit}
                    <span style={{ fontSize: 11, fontWeight: 500, color: 'rgba(26,106,133,0.7)' }}>Suggested</span>
                  </button>
                  <button onClick={() => stepAmount(step)} style={{
                    padding: '18px 8px', cursor: 'pointer', border: `1.5px solid ${BORDER}`,
                    borderRadius: 14, fontSize: 17, fontWeight: 700, background: CARD, color: TEXT,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}>
                    +{step}{unit}
                    <span style={{ fontSize: 11, fontWeight: 500, color: TEXT2 }}>Adjust</span>
                  </button>
                </div>
                  );
                })()}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 14 }}>
                  <Info size={12} color={TEXT2} />
                  <span style={{ fontSize: 12, color: TEXT2 }}>
                    {getModeFeedAmount() != null
                      ? 'Based on your usual amount'
                      : weights.length > 0
                      ? `Based on latest weight · ${(weights[weights.length - 1].grams / 1000).toFixed(2)}kg`
                      : babyDOB
                      ? `Based on date of birth · ${effectiveAge}w old`
                      : `Based on age · ${effectiveAge}w`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                  <input
                    type="number"
                    placeholder={`Custom (${unit})`}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    style={{ flex: 1, padding: '12px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 16, outline: 'none', color: TEXT, background: CARD }}
                  />
                  <button
                    onClick={() => { if (customAmount) { logFeed(parseFloat(customAmount)); setQuickLogModal(null); } }}
                    disabled={!customAmount}
                    style={{
                      padding: '12px 22px', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
                      cursor: customAmount ? 'pointer' : 'not-allowed',
                      background: customAmount ? ACCENT : BORDER, color: customAmount ? ACCENT_TEXT : TEXT2, opacity: customAmount ? 1 : 0.55,
                    }}
                  >Log</button>
                </div>
                {/* Formula calculator */}
                <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
                  <button onClick={() => setShowCalculator(v => !v)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showCalculator ? 12 : 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.6 }}>Formula Calculator</span>
                    {showCalculator ? <ChevronUp size={15} color={TEXT2} /> : <ChevronDown size={15} color={TEXT2} />}
                  </button>
                  {showCalculator && <>
                    <p style={{ margin: '0 0 12px', fontSize: 13, color: TEXT2 }}>For {convert(recommended)}{unit} per feed:</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {[
                        { value: calculateFormula(unit === 'ml' ? recommended : ozToMl(recommended)).scoops, label: 'Scoops' },
                        { value: convert(calculateFormula(unit === 'ml' ? recommended : ozToMl(recommended)).water), label: `${unit} Water` },
                      ].map(({ value, label }) => (
                        <div key={label} style={{ background: BG, borderRadius: 12, padding: '14px', textAlign: 'center' }}>
                          <div style={{ fontSize: 26, fontWeight: 700, color: ACCENT_TEXT, marginBottom: 2 }}>{value}</div>
                          <div style={{ fontSize: 11, color: TEXT2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                    <p style={{ margin: '10px 0 0', fontSize: 11, color: TEXT2, fontStyle: 'italic' }}>1 scoop per 30ml (1oz) water</p>
                  </>}
                </div>
                </>
                )}
              </>
            )}

            {quickLogModal === 'sleep' && (() => {
              const canLog = sleepStartDate && sleepStartTime && sleepEndDate && sleepEndTime
                && new Date(`${sleepEndDate}T${sleepEndTime}`) > new Date(`${sleepStartDate}T${sleepStartTime}`);
              const today = localDateStr(new Date());
              const fieldStyle = { width: '100%', height: 44, padding: '0 12px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 15, fontWeight: 600, outline: 'none', boxSizing: 'border-box', color: TEXT, background: CARD };
              // Editing targets a wake window (awake period). A new entry is a nap
              // (sleep period), so its fields read "fell asleep" → "woke up".
              const isNap = editingEntry?.type !== 'sleep';
              const startLabel = isNap ? 'Fell asleep' : 'Wake';
              const endLabel = isNap ? 'Woke up' : 'Sleep';
              return (
                <>
                  <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700, color: TEXT }}>{isNap ? 'Log a Nap' : 'Edit Wake Window'}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{startLabel} date</div>
                      <input type="date" value={sleepStartDate} max={today} onChange={e => setSleepStartDate(e.target.value)} style={fieldStyle} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{startLabel} time</div>
                      <input type="time" value={sleepStartTime} onChange={e => setSleepStartTime(e.target.value)} style={fieldStyle} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{endLabel} date</div>
                      <input type="date" value={sleepEndDate} max={today} onChange={e => setSleepEndDate(e.target.value)} style={fieldStyle} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{endLabel} time</div>
                      <input type="time" value={sleepEndTime} onChange={e => setSleepEndTime(e.target.value)} style={fieldStyle} />
                    </div>
                  </div>
                  {canLog && (() => {
                    const ms = new Date(`${sleepEndDate}T${sleepEndTime}`) - new Date(`${sleepStartDate}T${sleepStartTime}`);
                    const totalMin = Math.floor(ms / 60000);
                    const h = Math.floor(totalMin / 60), m = totalMin % 60;
                    return <div style={{ textAlign: 'center', fontSize: 13, color: TEXT2, marginBottom: 12 }}>{isNap ? 'Nap length' : 'Wake window'}: <span style={{ fontWeight: 700, color: ACCENT_TEXT }}>{h > 0 ? `${h}h ` : ''}{m}m</span></div>;
                  })()}
                  <button onClick={logSleepWindow} disabled={!canLog} style={{
                    width: '100%', padding: '13px', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
                    cursor: canLog ? 'pointer' : 'not-allowed', background: canLog ? ACCENT : BORDER, color: canLog ? ACCENT_TEXT : TEXT2, opacity: canLog ? 1 : 0.55,
                  }}>{isNap ? 'Log Nap' : 'Save Changes'}</button>
                </>
              );
            })()}

            {quickLogModal === 'diaper' && (() => {
              const POOP_COLORS = [
                { id: 'yellow',   label: 'Yellow',   hex: '#F5C842', note: 'Normal (breastfed)' },
                { id: 'mustard',  label: 'Mustard',  hex: '#C8A415', note: 'Normal' },
                { id: 'brown',    label: 'Brown',    hex: '#8B5E3C', note: 'Normal (formula)' },
                { id: 'green',    label: 'Green',    hex: '#4CAF50', note: 'Foremilk / diet' },
                { id: 'orange',   label: 'Orange',   hex: '#FF9500', note: 'Normal variation' },
                { id: 'black',    label: 'Black',    hex: '#2C2C2C', note: 'Meconium / iron' },
                { id: 'red',      label: 'Red',      hex: '#FF3B30', note: 'See doctor' },
                { id: 'white',    label: 'White',    hex: '#E5E5EA', note: 'See doctor' },
              ];
              const needsDirty = diaperPendingType === 'dirty' || diaperPendingType === 'both';
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    {diaperPendingType && (
                      <button onClick={() => setDiaperPendingType(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT2, fontSize: 20, lineHeight: 1, padding: 0 }}>‹</button>
                    )}
                    <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: TEXT }}>
                      {needsDirty ? 'Poop colour?' : editingEntry?.type === 'diaper' ? 'Edit Diaper' : 'Log a Diaper'}
                    </h3>
                  </div>

                  {!diaperPendingType && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                      {[
                        { type: 'wet',   label: 'Wet',  emoji: '💧' },
                        { type: 'dirty', label: 'Dirty', emoji: '💩' },
                        { type: 'both',  label: 'Both', emoji: '💧💩' },
                      ].map(({ type, label, emoji }) => (
                        <button key={type} onClick={() => type === 'wet' ? logDiaper(type) : setDiaperPendingType(type)} style={{
                          padding: '20px 8px', border: `1.5px solid ${BORDER}`, borderRadius: 14,
                          cursor: 'pointer', background: CARD, display: 'flex', flexDirection: 'column',
                          alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: TEXT,
                        }}>
                          <span style={{ fontSize: 28 }}>{emoji}</span>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}

                  {needsDirty && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
                        {POOP_COLORS.map(({ id, label, hex, note }) => (
                          <button key={id} onClick={() => logDiaper(diaperPendingType, id)} style={{
                            padding: '12px 4px', border: `1.5px solid ${BORDER}`, borderRadius: 12,
                            cursor: 'pointer', background: CARD, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', gap: 6,
                          }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: hex, border: id === 'white' ? `1.5px solid ${BORDER}` : 'none' }} />
                            <span style={{ fontSize: 11, fontWeight: 600, color: TEXT }}>{label}</span>
                          </button>
                        ))}
                      </div>
                      <button onClick={() => logDiaper(diaperPendingType)} style={{
                        width: '100%', padding: '11px', border: `1.5px solid ${BORDER}`, borderRadius: 10,
                        fontSize: 14, fontWeight: 600, color: TEXT2, background: 'none', cursor: 'pointer',
                      }}>Skip colour</button>
                    </>
                  )}
                </>
              );
            })()}

            {quickLogModal === 'pump' && (() => {
              const hasAny = pumpLeft || pumpRight;
              const totalPreview = (parseFloat(pumpLeft) || 0) + (parseFloat(pumpRight) || 0);
              return (
                <>
                  <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700, color: TEXT }}>{editingEntry?.type === 'pump' ? 'Edit Pump' : 'Log a Pump'}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    {[
                      { side: 'L', label: 'Left breast', val: pumpLeft, set: setPumpLeft },
                      { side: 'R', label: 'Right breast', val: pumpRight, set: setPumpRight },
                    ].map(({ side, label, val, set }) => (
                      <div key={side}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number"
                            placeholder="0"
                            value={val}
                            onChange={(e) => set(e.target.value)}
                            style={{ width: '100%', padding: '12px 36px 12px 14px', border: `1.5px solid ${val ? GREEN : BORDER}`, borderRadius: 10, fontSize: 18, fontWeight: 700, outline: 'none', color: TEXT, background: CARD, boxSizing: 'border-box' }}
                          />
                          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: TEXT2 }}>{unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {hasAny && (
                    <div style={{ textAlign: 'center', fontSize: 13, color: TEXT2, marginBottom: 12 }}>
                      Total: <span style={{ fontWeight: 700, color: GREEN }}>{convert(totalPreview)}{unit}</span>
                    </div>
                  )}
                  <button
                    onClick={() => { if (hasAny) logPump(pumpLeft, pumpRight); }}
                    disabled={!hasAny}
                    style={{
                      width: '100%', padding: '13px', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
                      cursor: hasAny ? 'pointer' : 'not-allowed',
                      background: hasAny ? GREEN : BORDER, color: 'white', opacity: hasAny ? 1 : 0.55,
                    }}
                  >Log Session</button>
                </>
              );
            })()}

            {quickLogModal === 'weight' && (() => {
              const totalGrams = (parseInt(weightInput) || 0) * 1000 + (parseInt(weightG) || 0);
              const canLog = totalGrams > 0;
              return (
                <>
                  <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700, color: TEXT }}>{editingEntry?.type === 'weight' ? 'Edit Weight' : 'Log Weight'}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    {[
                      { label: 'Kilograms', placeholder: '0', value: weightInput, set: setWeightInput },
                      { label: 'Grams', placeholder: '0', value: weightG, set: setWeightG },
                    ].map(({ label, placeholder, value, set }) => (
                      <div key={label}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
                        <div style={{ position: 'relative' }}>
                          <input type="number" placeholder={placeholder} value={value} onChange={e => set(e.target.value)}
                            style={{ width: '100%', padding: '12px 36px 12px 14px', border: `1.5px solid ${value ? WEIGHT_COLOR : BORDER}`, borderRadius: 10, fontSize: 18, fontWeight: 700, outline: 'none', color: TEXT, background: CARD, boxSizing: 'border-box' }} />
                          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: TEXT2 }}>{label === 'Kilograms' ? 'kg' : 'g'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {canLog && (
                    <div style={{ textAlign: 'center', fontSize: 13, color: TEXT2, marginBottom: 12 }}>
                      Total: <span style={{ fontWeight: 700, color: WEIGHT_COLOR }}>{(totalGrams / 1000).toFixed(3).replace(/\.?0+$/, '')}kg ({totalGrams}g)</span>
                    </div>
                  )}
                  <button onClick={() => { if (canLog) logWeight(totalGrams); }} disabled={!canLog} style={{
                    width: '100%', padding: '13px', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
                    cursor: canLog ? 'pointer' : 'not-allowed', background: canLog ? WEIGHT_COLOR : BORDER, color: 'white', opacity: canLog ? 1 : 0.55,
                  }}>Log</button>
                </>
              );
            })()}

            {quickLogModal === 'medicine' && (
              <>
                <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700, color: TEXT }}>{editingEntry?.type === 'medicine' ? 'Edit Medicine' : 'Log Medicine'}</h3>
                <input
                  type="text"
                  placeholder="Medicine name (e.g. Vitamin D)"
                  value={medicineName}
                  onChange={(e) => setMedicineName(e.target.value)}
                  style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 16, outline: 'none', color: TEXT, background: CARD, marginBottom: 10, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Dose (optional, e.g. 1 drop)"
                    value={medicineDose}
                    onChange={(e) => setMedicineDose(e.target.value)}
                    style={{ flex: 1, padding: '12px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 16, outline: 'none', color: TEXT, background: CARD }}
                  />
                  <button
                    onClick={() => { if (medicineName) logMedicine(medicineName.trim(), medicineDose.trim()); }}
                    disabled={!medicineName}
                    style={{
                      padding: '12px 22px', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
                      cursor: medicineName ? 'pointer' : 'not-allowed',
                      background: medicineName ? MED_COLOR : BORDER, color: 'white', opacity: medicineName ? 1 : 0.55,
                    }}
                  >Log</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Entry action sheet */}
      {entryActionSheet && (() => {
        const { type, entry } = entryActionSheet;
        const title = type === 'feed' ? 'Feed'
          : type === 'sleep' ? 'Wake Window'
          : type === 'nap' ? 'Nap'
          : type === 'diaper' ? 'Diaper'
          : type === 'pump' ? 'Pump Session'
          : type === 'weight' ? 'Weight'
          : 'Medicine';
        const summary = type === 'feed'
          ? `${entry.latch ? `${latchLabel(entry).total} latch` : `${convert(entry.amount)}${unit}`} · ${new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
          : type === 'sleep' || type === 'nap'
          ? `${new Date(entry.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} – ${new Date(entry.end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
          : type === 'diaper'
          ? `${entry.type.charAt(0).toUpperCase() + entry.type.slice(1)}${entry.color ? ` · ${entry.color}` : ''} · ${new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
          : type === 'pump'
          ? `${convert(entry.amount)}${unit} total · ${new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
          : type === 'weight'
          ? `${(entry.grams / 1000).toFixed(3).replace(/\.?0+$/, '')}kg · ${new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
          : `${entry.name}${entry.dose ? ` · ${entry.dose}` : ''} · ${new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
            onClick={() => setEntryActionSheet(null)}>
            <div style={{ background: CARD, borderRadius: '20px 20px 0 0', padding: '16px 20px 40px', maxWidth: 430, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ width: 36, height: 4, background: BORDER, borderRadius: 2, margin: '0 auto 20px' }} />
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: TEXT, marginBottom: 4 }}>{title}</div>
                <div style={{ fontSize: 14, color: TEXT2 }}>{summary}</div>
              </div>
              {/* A latch entry is durations from the timer — nothing to edit by hand. */}
              {!(type === 'feed' && entry.latch) && type !== 'nap' && (
                <button onClick={() => openEditEntry(type, entry)}
                  style={{ width: '100%', padding: '14px', border: `1.5px solid ${BORDER}`, borderRadius: 12, fontSize: 15, fontWeight: 600, background: CARD, color: TEXT, marginBottom: 10, cursor: 'pointer' }}>
                  Edit Entry
                </button>
              )}
              <button onClick={() => deleteEntry(type, entry)}
                style={{ width: '100%', padding: '14px', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, background: RED, color: 'white', cursor: 'pointer' }}>
                Delete Entry
              </button>
              <button onClick={() => setEntryActionSheet(null)}
                style={{ width: '100%', padding: '14px', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, background: 'none', color: TEXT2, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        );
      })()}

      {/* Bottom tab bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: 430,
        background: isDark ? 'rgba(28,28,30,0.92)' : 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: `1px solid ${BORDER}`,
        display: 'flex',
        paddingBottom: 'env(safe-area-inset-bottom, 8px)',
        zIndex: 100,
      }}>
        {tabs.map(({ id, label, Icon, beta }) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            flex: 1, padding: '10px 0 8px', border: 'none', background: 'none', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            color: activeTab === id ? ACCENT_TEXT : TEXT2,
          }}>
            {/* Beta sits on the icon rather than in the label: the label row is
                only 11px and a fifth tab leaves no width to spare. */}
            <div style={{ position: 'relative', display: 'flex' }}>
              <Icon size={23} strokeWidth={activeTab === id ? 2.5 : 1.75} />
              {beta && (
                <span style={{
                  position: 'absolute', top: -5, left: '100%', marginLeft: 1,
                  fontSize: 7, fontWeight: 800, letterSpacing: 0.3, lineHeight: 1,
                  padding: '2px 3px', borderRadius: 4, whiteSpace: 'nowrap',
                  background: activeTab === id ? ACCENT_TEXT : TEXT2,
                  color: isDark ? '#000' : '#FFF',
                }}>BETA</span>
              )}
            </div>
            <span style={{ fontSize: 11, fontWeight: activeTab === id ? 700 : 500 }}>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
